import { useState, useEffect, useMemo, useRef, useCallback, Fragment } from "react";
import { consolidateTickets, shouldWheel, applyExactaSingleMin, comboToXpress, amwagerComboField, actualPlayTotal, singleMinNote } from "../lib/ticketConsolidation";
import { AdwButton } from "./AdwButton";

// Temporarily hidden while we accumulate a few days of live 1MTP odds. Flip to true to re-enable.
const SHOW_OVERBET_FAV = true;

import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import "../styles/saddlecloth.css";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "./ui/table";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "./ui/tooltip";
import { formatML } from "../lib/odds";
import BetMakerPreferences from "./BetMakerPreferences";
import { AppMenu } from "./AppMenu";
import { Toaster, toast } from "./ui/sonner";
import { Loader2, Download, User, LogOut, Sparkles, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Info, RotateCcw, X, AlertTriangle, Settings, Check, Upload, BookOpen, PlayCircle, ArrowLeftRight, Sun, Moon, Zap } from "lucide-react";
import { useTriPlaysDay } from "../lib/useTriPlays";
import TrialBadge from "./TrialBadge";

import { overbetTip } from "../lib/overbetStats";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (date) => {
  const p = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York", year: "numeric", month: "2-digit",
    day: "2-digit", hour: "2-digit", hour12: false,
  }).formatToParts(date).reduce((a, x) => { a[x.type] = x.value; return a; }, {});
  let h = parseInt(p.hour, 10); if (h === 24) h = 0;
  const dt = new Date(Date.UTC(+p.year, +p.month - 1, +p.day));
  if (h < 4) dt.setUTCDate(dt.getUTCDate() - 1);
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}-${String(dt.getUTCDate()).padStart(2, "0")}`;
};

const pnClass = (n) => {
  const num = parseInt(n, 10);
  return `pn-badge ${num >= 1 && num <= 24 ? `pn${num}` : "pn-default"}`;
};

const POOLS = [
  { code: "EX", name: "Exacta", positions: 2 },
  { code: "TRI", name: "Trifecta", positions: 3 },
  { code: "SPR", name: "Superfecta", positions: 4 },
];
// Per-pool minimum bet (mirrors backend POOL_CONFIG) — used for the live
// round-up "min coverage" estimate shown next to each pool's stake.
const POOL_MIN = { EX: 2, TRI: 0.5, SPR: 0.1 };
// Short-field floor: <=3 active runners => no Exacta; <=4 => no Tri/Super.
const POOL_MIN_FIELD = { EX: 4, TRI: 5, SPR: 5 };
const poolOfferedAtField = (pool, fs) => !fs || fs >= (POOL_MIN_FIELD[pool] || 0);
const BANKROLL_PRESETS = [20, 30, 40, 50, 60, 80, 100, 150, 200, 300, 500];
const BLENDER_ENABLED = false;
// Win Rank column: collapses the 4 pool-score columns (DBL/P3/P4/P5) into a single
// consensus-ranked ordinal (1st/2nd/3rd...). Flip to false to restore the legacy
// 4-column pool-score grid (easy revert if clients prefer the old view).
const WIN_RANK_ENABLED = true;
const POOL_COLS = WIN_RANK_ENABLED ? 1 : 4;
// Ordinal string: 1 -> "1st", 2 -> "2nd", 11 -> "11th", etc.
const ordinal = (n) => {
  const s = ["th", "st", "nd", "rd"], v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};
// Horizontal pools (DD/P3/P4/P5/P6) — data + layout are wired but hidden until
// the horizontal ticket engine lands. Flip to true to reveal the rows.
const HORIZONTALS_ENABLED = false;
const HORIZONTAL_POOLS = [
  { code: "DD", name: "Daily Double" },
  { code: "P3", name: "Pick 3" },
  { code: "P4", name: "Pick 4" },
  { code: "P5", name: "Pick 5" },
  { code: "P6", name: "Pick 6" },
];
const POS_LABELS = ["1st", "2nd", "3rd", "4th"];

// Normalize a combo/winners string for payout matching ("1 / 2" -> "1-2").
const normalizeCombo = (c) => (c || "").toString().trim().replace(/\s+/g, "").replace(/\//g, "-").toLowerCase();
// Canonical payout-pool name (payouts arrive already lowercased: exacta/tri/spr).
const POOL_TO_PAYOUT = { EX: "exacta", TRI: "tri", SPR: "spr" };

// Collapsible guide — mode-specific copy. NOTE (2026-07-02): text is a first pass and
// needs an owner review before re-enabling. Toggle GUIDE_ENABLED to show/hide the button.
const GUIDE_ENABLED = false;
const GUIDE_BY_MODE = {
  freeflow: [
    ["Overview", "Free Flow: every runner is eligible for every finishing position by default — you shape tickets by RESTRICTING positions, not building them up. Do nothing and you get the full weighted spread. Weighting uses the MyOdds line you build; leave MyOdds blank to use Live or Projected odds. A MyOdds value lower than the Live/Projected value puts more money on that horse; higher puts less."],
    ["Pool", "Select one or more pools (Exacta / Trifecta / Superfecta). With multiple pools the bankroll splits evenly, or use 'Per Pool' to give each the full bankroll."],
    ["BankRoll", "The total dollars wagered, before round-up."],
    ["Use Projected / Live", "Which line drives weighting where MyOdds is blank. (No projected odds for race 1 — falls back to Live.)"],
    ["Best Finish", "The best (highest) position a horse can be used in. 2nd = never used 1st; 3rd = never 1st or 2nd, etc. 'Any' = no restriction. Click the column header to reset all runners."],
    ["Worst Finish", "The worst (lowest) position a horse can be used in. Example: to use a horse in 1st only, set Best Finish = 1st and Worst Finish = 1st. Click the column header to reset all runners."],
    ["1st / 2nd / 3rd / 4th dots", "Click a position dot to add or remove a runner's eligibility for that finishing spot — a filled green dot means eligible, a dashed circle means excluded. This stays in sync with the Best/Worst Finish dropdowns and updates your ticket structure live."],
    ["Dont Use", "Excludes the horse from all tickets entirely."],
  ],
  traditional: [
    ["Overview", "ADW: a positional grid where you CHECK each runner into the finishing positions you want it used in. Nothing is included until you check it. Weighting uses the MyOdds line you build; leave MyOdds blank to use Live or Projected odds."],
    ["Pool", "Select one or more pools (Exacta / Trifecta / Superfecta). With multiple pools the bankroll splits evenly, or use 'Per Pool' to give each the full bankroll."],
    ["BankRoll", "The total dollars wagered, before round-up."],
    ["Use Projected / Live", "Which line drives weighting where MyOdds is blank. (No projected odds for race 1 — falls back to Live.)"],
    ["All", "Checks a runner into every finishing position at once (a full box for that horse). This is a convenience toggle, not a key."],
    ["1st / 2nd / 3rd / 4th", "Check the finishing positions you want each runner used in."],
    ["Column tools (ALL / >)", "The green ALL button includes every runner in that position column; the > button copies a column's selections into the next position."],
    ["SWAP (1-2, 1-3, 2-3)", "Swaps the checked selections between two position columns in one click."],
    ["Dont Use", "Excludes the horse from all tickets entirely (overrides any checks)."],
  ],
};

// Display a clean post time from either "HH:MM AM" or an ISO timestamp.
const fmtPost = (pt) => {
  if (!pt) return "";
  if (pt.includes("T")) {
    const d = new Date(pt);
    if (!isNaN(d)) return d.toLocaleTimeString("en-US", { timeZone: "America/New_York", hour: "numeric", minute: "2-digit" });
  }
  return pt;
};

// Minutes-To-Post — mirrors QuickPickTester.calculateMTP/formatMTP.
const calcMTP = (timeStr, dateStr) => {
  if (!timeStr) return null;
  try {
    let postTime;
    if (!/^\d{1,2}:\d{2}$/.test(String(timeStr).trim())) {
      postTime = new Date(timeStr);
    } else {
      const dateToUse = dateStr || formatDate(new Date());
      const estDateStr = `${dateToUse}T${String(timeStr).padStart(5, "0")}:00`;
      const month = new Date(dateToUse).getMonth();
      const offset = (month >= 2 && month <= 10) ? "-04:00" : "-05:00";
      postTime = new Date(estDateStr + offset);
    }
    if (isNaN(postTime.getTime())) return null;
    return Math.round((postTime.getTime() - Date.now()) / 60000);
  } catch (e) {
    return null;
  }
};
const fmtMTP = (timeStr, dateStr) => {
  const mtp = calcMTP(timeStr, dateStr);
  if (mtp === null) return { text: "", urgent: false };
  if (mtp < 0) return { text: "POST", urgent: false };
  if (mtp === 0) return { text: "NOW", urgent: true };
  return { text: `${mtp} MTP`, urgent: mtp <= 2 };
};

// Legacy BetMaker rank-class colors (per score column).
const RANK_COLORS = {
  rank1: "rgb(99,191,25)",   // best positive
  rank2: "rgb(177,222,190)", // 2nd positive
  plus: "rgb(225,242,230)",  // other positives
  n1: "rgb(255,0,0)",        // worst (most negative)
  n2: "rgb(255,128,64)",     // 2nd most negative
  neg: "rgb(255,190,159)",   // other negatives
};
// Assign each active runner a rank class for a given score field, per legacy logic.
const computeRankColors = (entries, field) => {
  const vals = entries
    .filter((e) => !e.scratched && e[field] != null && Math.abs(e[field]) >= 0.05)
    .map((e) => ({ pn: String(e.programNumber), v: e[field] }));
  const pos = vals.filter((x) => x.v > 0).sort((a, b) => b.v - a.v);
  const neg = vals.filter((x) => x.v < 0).sort((a, b) => a.v - b.v);
  const map = {};
  pos.forEach((x, i) => { map[x.pn] = i === 0 ? "rank1" : i === 1 ? "rank2" : "plus"; });
  neg.forEach((x, i) => { map[x.pn] = i === 0 ? "n1" : i === 1 ? "n2" : "neg"; });
  return map;
};
const rankStyle = (map, pn) => {
  const c = map[pn];
  return c ? { backgroundColor: RANK_COLORS[c], color: "#111", fontWeight: 700 } : {};
};

// Render a pool-score cell. When the pool was dropped (uncovered), covered
// runners show blank and only the specific uncovered runner shows "uncvd".
const ScoreCell = ({ value, uncovered, poolDropped, colorMap, pn, scratched }) => {
  if (poolDropped) {
    return (
      <TableCell className="text-center text-xs border border-slate-300">
        {uncovered && !scratched ? <span className="font-semibold text-slate-500" data-testid={`score-uncovered-${pn}`}>uncvd</span> : ""}
      </TableCell>
    );
  }
  return (
    <TableCell className="text-center text-xs border border-slate-300" style={rankStyle(colorMap, pn)}>
      {value?.toFixed(1) ?? ""}
    </TableCell>
  );
};

// Win Rank cell — a single consensus-ranked ordinal (1st/2nd/3rd...), color-coded
// by placement (top-2 greens, bottom-2 orange/red). Blank for scratched runners or
// those without a consensus signal.
const WinRankCell = ({ info, scratched, pn }) => {
  if (scratched || !info) {
    return <TableCell className="text-center text-xs border border-slate-300" data-testid={`winrank-${pn}`} />;
  }
  const style = info.tier
    ? { backgroundColor: RANK_COLORS[info.tier], color: "#111", fontWeight: 700 }
    : {};
  return (
    <TableCell className="text-center text-xs border border-slate-300" style={style} data-testid={`winrank-${pn}`}>
      {info.ord}
    </TableCell>
  );
};

// Pool-score column header with a "U" badge when the pool was dropped (uncovered).
const PoolHead = ({ label, dropped }) => (
  <TableHead className="text-emerald-700 text-center border border-slate-300">
    <span className="inline-flex items-center gap-1 justify-center">
      {label}
      {dropped && (
        <span title="Uncovered Runners - Pool not Scored" data-testid={`pool-uncovered-${label}`}
          className="px-1 rounded bg-amber-100 text-amber-700 text-[10px] font-bold">U</span>
      )}
    </span>
  </TableHead>
);

// --- Race Payoffs card (light theme, mirrors the main-app RacePayoffs panel) ---
const PAYOFF_POOL_ORDER = ["exacta", "tri", "spr", "dbl", "p3", "p4", "p5", "p6", "quinella", "omni", "pen"];
const PAYOFF_POOL_LABELS = {
  exacta: "Exacta", tri: "Trifecta", spr: "Superfecta", dbl: "Daily Double",
  p3: "Pick 3", p4: "Pick 4", p5: "Pick 5", p6: "Pick 6",
  quinella: "Quinella", omni: "Omni", pen: "Pentafecta",
};
const isWPS = (pool) => ["win", "place", "show"].includes(pool);

const PayoffCard = ({ payouts, entries }) => {
  if (!payouts || payouts.length === 0) return null;
  const byNum = {};
  payouts.forEach((p) => {
    if (isWPS(p.pool)) {
      const n = (p.winners || "").toString().trim();
      byNum[n] = byNum[n] || { num: n };
      byNum[n][p.pool] = p.payout;
    }
  });
  const nameFor = (n) => entries?.find((x) => String(x.programNumber) === n)?.horseName || `Horse ${n}`;
  const finishers = Object.values(byNum).sort((a, b) => {
    const rank = (x) => (x.win != null ? 0 : x.place != null ? 1 : 2);
    return rank(a) - rank(b);
  });
  const poolPayoffs = payouts.filter((p) => !isWPS(p.pool)).sort((a, b) => {
    const ia = PAYOFF_POOL_ORDER.indexOf(a.pool); const ib = PAYOFF_POOL_ORDER.indexOf(b.pool);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  });
  const fmt2 = (v) => (v != null ? `$${(Number(v) * 2).toFixed(2)}` : "-");

  return (
    <div className="rounded-lg border border-slate-200 p-3 bg-slate-50" data-testid="race-payoffs-card">
      <div className="text-xs font-extrabold uppercase tracking-wider text-emerald-600 mb-2">Race Payoffs</div>
      {finishers.length > 0 && (
        <div className="mb-3">
          <div className="grid grid-cols-[auto_1fr_48px_48px_48px] gap-1 text-[10px] uppercase text-slate-400 px-1 pb-1">
            <span>#</span><span>Horse</span>
            <span className="text-right">Win</span><span className="text-right">Plc</span><span className="text-right">Shw</span>
          </div>
          {finishers.map((f) => (
            <div key={f.num} className="grid grid-cols-[auto_1fr_48px_48px_48px] gap-1 items-center text-xs px-1 py-1 border-b border-slate-200" data-testid={`payoff-runner-${f.num}`}>
              <span className={pnClass(f.num)}>{f.num}</span>
              <span className="text-slate-700 truncate">{nameFor(f.num)}</span>
              <span className={`text-right ${f.win != null ? "text-emerald-600 font-semibold" : "text-slate-300"}`}>{fmt2(f.win)}</span>
              <span className={`text-right ${f.place != null ? "text-emerald-600 font-semibold" : "text-slate-300"}`}>{fmt2(f.place)}</span>
              <span className={`text-right ${f.show != null ? "text-emerald-600 font-semibold" : "text-slate-300"}`}>{fmt2(f.show)}</span>
            </div>
          ))}
        </div>
      )}
      {poolPayoffs.length > 0 && (
        <div>
          <div className="text-xs font-extrabold uppercase tracking-wider text-emerald-600 mb-1 px-1">Race Payoff ($1)</div>
          {poolPayoffs.map((p, i) => (
            <div key={i} className="grid grid-cols-[90px_1fr_auto] gap-1 items-center text-xs px-1 py-1 border-b border-slate-200" data-testid={`payoff-pool-${p.pool}`}>
              <span className="text-slate-500">{PAYOFF_POOL_LABELS[p.pool] || (p.pool || "").toUpperCase()}</span>
              <span className="font-mono text-slate-700 truncate">{(p.winners || "").toString().trim()}</span>
              <span className="text-right text-emerald-600 font-semibold">${Number(p.payout).toFixed(2)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const amwagerLines = (track, race, pool, tickets, date, wheel = true, base, ticketMin) => {
  const d = date.replace(/-/g, "");
  const pre = track.slice(0, -1), suf = track.slice(-1);
  const cons = consolidateTickets(tickets, wheel);
  applyExactaSingleMin(cons, pool, base, ticketMin);
  return cons.map((t) => `,,${d},USA,${pre},${suf},${pool},,${race},${amwagerComboField(t.legs)},USD,${t.amount.toFixed(2)},,,`).join("\n");
};
const xpressLines = (track, race, pool, tickets, date, wheel = true, base, ticketMin) => {
  const names = { EX: "EXACTA", TRI: "TRIFECTA", SPR: "SUPERFECTA" };
  const cons = consolidateTickets(tickets, wheel);
  applyExactaSingleMin(cons, pool, base, ticketMin);
  return cons.map((t) => `${date},${track},${race},${names[pool] || pool},${comboToXpress(t.legs)},${t.amount.toFixed(2)},,,WHEEL`).join("\n");
};
const downloadFile = (content, filename) => {
  const blob = new Blob([content], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export default function BetMaker() {
  const { user, logout, hasBetmaker } = useAuth();
  const navigate = useNavigate();

  // Gate: admins always; other users need the betmaker_enabled flag.
  useEffect(() => {
    if (user && !hasBetmaker()) navigate("/");
  }, [user, hasBetmaker, navigate]);

  const [selectedDate, setSelectedDate] = useState(formatDate(new Date()));
  const [tracks, setTracks] = useState([]);
  const [selectedTrack, setSelectedTrack] = useState("");
  const [selectedRace, setSelectedRace] = useState("");
  const [entries, setEntries] = useState([]);
  const [loadingEntries, setLoadingEntries] = useState(false);

  const [poolSel, setPoolSel] = useState({ EX: false, TRI: true, SPR: false });
  const [consolThresholds, setConsolThresholds] = useState({ EX: 0, TRI: 0, SPR: 0 });
  const [bankroll, setBankroll] = useState("50");            // global default (per-pool fallback)
  const [poolAmt, setPoolAmt] = useState({ EX: "", TRI: "", SPR: "" }); // per-pool $ boxes (strings)
  const [poolDefaults, setPoolDefaults] = useState({});      // saved per-pool defaults from prefs
  const [focusedPool, setFocusedPool] = useState(null);      // which pool box the presets target
  const [oddsSource, setOddsSource] = useState("proj");
  const [blendPct, setBlendPct] = useState(0);
  const [roundUp, setRoundUp] = useState(true);

  const [grid, setGrid] = useState({});
  const [dontUse, setDontUse] = useState({});
  const [mode, setMode] = useState("freeflow");      // "freeflow" | "traditional"
  const [showGuide, setShowGuide] = useState(false);
  const [hintDismissed, setHintDismissed] = useState(() => {
    try { return localStorage.getItem("bm_hint_dismissed") === "1"; } catch { return false; }
  });
  const dismissHint = () => { try { localStorage.setItem("bm_hint_dismissed", "1"); } catch {} setHintDismissed(true); };
  const [bestFin, setBestFin] = useState({});
  const [worstFin, setWorstFin] = useState({});
  const [keySel, setKeySel] = useState({});
  // Straight wagers (Win / Win+Place) — per-runner, in addition to the combination pools.
  const [straightAmt, setStraightAmt] = useState({ WIN: "", WP: "" });        // per-runner amount
  const [straightBet, setStraightBet] = useState({});                          // pn -> 'WIN' | 'WP'
  const [result, setResult] = useState(null);
  const [toppedUp, setToppedUp] = useState([]);   // pool codes currently Complete-Coverage topped up
  const [racePayouts, setRacePayouts] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  // ── User preferences (Default Settings) ─────────────────────────────
  const DEFAULT_PREFS = { defaultApp: "intellitix", defaultMode: "freeflow", bankroll: 50, poolDefaults: {}, pools: ["TRI"], oddsSource: "proj", blendPct: 0, perPool: false, roundUp: true, rememberLastUsed: false, rememberRaceInputs: true, showTopUp: true, theme: "bright" };
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const [betmakerEnabled, setBetmakerEnabled] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);

  const applyPrefs = (p) => {
    setMode(p.defaultMode || "freeflow");
    setBankroll(String(p.bankroll ?? 50));
    const sel = { EX: p.pools?.includes("EX") || false, TRI: p.pools?.includes("TRI") || false, SPR: p.pools?.includes("SPR") || false };
    setPoolSel(sel);
    const pd = p.poolDefaults || {};
    setPoolDefaults(pd);
    // Seed each selected pool's box from its saved default (or the global default).
    const seed = {};
    ["EX", "TRI", "SPR"].forEach((c) => { seed[c] = sel[c] ? String(pd[c] ?? p.bankroll ?? 50) : ""; });
    setPoolAmt(seed);
    setOddsSource(p.oddsSource || "proj");
    setBlendPct(p.blendPct ?? 0);
    setRoundUp(p.roundUp !== false);
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/betmaker/preferences`, { withCredentials: true });
        const p = { ...DEFAULT_PREFS, ...(r.data?.preferences || {}) };
        setPrefs(p);
        setBetmakerEnabled(!!r.data?.betmakerEnabled);
        applyPrefs(p);
      } catch { /* not logged in / no prefs -> keep defaults */ }
      try { const last = localStorage.getItem("bm_odds_source"); if (last === "proj" || last === "live") setOddsSource(last); } catch {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/quickpick/consolidation-settings`);
        if (r.data?.thresholds) setConsolThresholds(r.data.thresholds);
      } catch { /* keep defaults (0 = always consolidate) */ }
    })();
  }, []);


  const toggleTheme = async () => {
    const next = prefs.theme === "soft" ? "bright" : "soft";
    setPrefs((p) => ({ ...p, theme: next }));
    try {
      await axios.put(`${API}/betmaker/preferences`, { theme: next }, { withCredentials: true });
    } catch { /* best-effort save; UI already reflects the change */ }
  };

  const savePrefs = async (form) => {
    try {
      const r = await axios.put(`${API}/betmaker/preferences`, form, { withCredentials: true });
      const p = { ...DEFAULT_PREFS, ...(r.data?.preferences || {}) };
      setPrefs(p);
      applyPrefs(p);
      try { localStorage.setItem("bm_odds_source", p.oddsSource || "proj"); } catch {}
      setPrefsOpen(false);
      toast.success("Default settings saved");
    } catch {
      toast.error("Could not save settings");
    }
  };

  const selectedTrackData = tracks.find((t) => t.code === selectedTrack);
  const selectedPools = POOLS.filter((p) => poolSel[p.code]);
  const maxPositions = Math.max(2, ...selectedPools.map((p) => p.positions));
  // Widest position set across ALL pools (e.g. 4 for SPR) — used to render the
  // Best/Worst Finish dropdown options so a set value (e.g. 4th) STICKS visually
  // regardless of which pools are currently selected. Eligibility/green dots still
  // respect the selected pools via `maxPositions`.
  const allPositions = Math.max(2, ...POOLS.map((p) => p.positions));

  // Resolved stake for a pool: the box value if set, else its saved default,
  // else the global default. This is the target the engine funds the pool with.
  const resolvedAmt = (code) => {
    const v = parseFloat(poolAmt[code]);
    if (!isNaN(v) && v > 0) return v;
    const d = parseFloat(poolDefaults[code]);
    if (!isNaN(d) && d > 0) return d;
    return parseFloat(bankroll) || 0;
  };
  // Check/uncheck a pool; seed its box from the default on first check.
  const togglePool = (code, on) => {
    setPoolSel((s) => ({ ...s, [code]: on }));
    if (on) {
      setPoolAmt((a) => (a[code] ? a : { ...a, [code]: String(poolDefaults[code] ?? bankroll ?? 50) }));
      setFocusedPool(code);
    }
  };
  const resolvedStraightAmt = (type) => {
    const v = parseFloat(straightAmt[type]);
    if (!isNaN(v) && v > 0) return v;
    const d = parseFloat(poolDefaults[type]);
    if (!isNaN(d) && d > 0) return d;
    return parseFloat(bankroll) || 0;
  };
  const sortPn = (a, b) => ((parseInt(a, 10) || 0) - (parseInt(b, 10) || 0)) || String(a).localeCompare(String(b));
  const winRunners = Object.keys(straightBet).filter((pn) => straightBet[pn] === "WIN").sort(sortPn);
  const wpRunners = Object.keys(straightBet).filter((pn) => straightBet[pn] === "WP").sort(sortPn);
  const winStake = winRunners.length * resolvedStraightAmt("WIN");
  const wpStake = wpRunners.length * resolvedStraightAmt("WP") * 2;
  // Pool + straight cells rendered as helpers so they can be laid out in a compact grid.
  const renderPoolCell = (code) => {
    const p = POOLS.find((x) => x.code === code);
    if (!p) return <div />;
    const offered = poolOfferedAtField(p.code, fieldSize);
    const on = poolSel[p.code] && offered;
    return (
      <div className={`flex items-center gap-2 text-sm ${!offered ? "opacity-50" : ""}`} data-testid={`pool-row-${p.code}`}>
        <label className={`flex items-center gap-2 text-slate-800 w-24 shrink-0 ${offered ? "cursor-pointer" : "cursor-not-allowed"}`} data-testid={`pool-toggle-${p.code}`}>
          <Checkbox checked={on} disabled={!offered} onCheckedChange={(c) => offered && togglePool(p.code, !!c)} />
          {p.name}
        </label>
        <div className="relative">
          <span className={`absolute left-2 top-1/2 -translate-y-1/2 text-xs ${on ? "text-slate-400" : "text-slate-300"}`}>$</span>
          <Input type="number" min="1" disabled={!on}
            value={on ? poolAmt[p.code] ?? "" : ""}
            placeholder={String(poolDefaults[p.code] ?? bankroll ?? "")}
            onFocus={() => setFocusedPool(p.code)}
            onChange={(e) => setPoolAmt((a) => ({ ...a, [p.code]: e.target.value }))}
            data-testid={`pool-amount-${p.code}`}
            className={`w-16 pl-5 text-right bg-white border-slate-300 text-slate-900 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${focusedPool === p.code && on ? "ring-2 ring-amber-400 border-amber-400" : ""} disabled:bg-slate-50 disabled:text-slate-300`} />
        </div>
        {!offered && fieldSize > 0 && (
          <span className="text-[11px] text-amber-600 italic" data-testid={`pool-not-offered-${p.code}`}>n/a · field {fieldSize}</span>
        )}
      </div>
    );
  };
  const renderStraightCell = (code, name) => (
    <div className="flex items-center gap-2 text-sm" data-testid={`straight-row-${code}`}>
      <span className="font-bold text-slate-800 w-8 shrink-0">{name}</span>
      <div className="relative">
        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">$</span>
        <Input type="number" min="1"
          value={straightAmt[code] ?? ""}
          placeholder={String(poolDefaults[code] ?? bankroll ?? "")}
          onFocus={(e) => { setFocusedPool(code); e.target.select(); }}
          onChange={(e) => setStraightAmt((a) => ({ ...a, [code]: e.target.value }))}
          data-testid={`straight-amount-${code}`}
          className={`w-16 pl-5 text-right bg-white border-slate-300 text-slate-900 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${focusedPool === code ? "ring-2 ring-amber-400 border-amber-400" : ""}`} />
      </div>
    </div>
  );
  const totalStake = selectedPools.reduce((sum, p) => sum + resolvedAmt(p.code), 0) + winStake + wpStake;

  // Clear the validation message AND any rendered tickets on the next user input
  // (any selection/config change), so stale tickets never linger.
  useEffect(() => {
    setError((e) => (e ? "" : e));
    setResult((r) => (r ? null : r));
  }, [keySel, bestFin, worstFin, dontUse, grid, poolSel, selectedTrack, selectedRace,
      poolAmt, oddsSource, blendPct, selectedDate, straightBet, straightAmt]);

  // Fetch the day's tracks/races. `resetSelection` clears the loaded race
  // (used on date change); the periodic auto-refresh passes false so the
  // user's current race stays put while Up Next (MTP + finished-race
  // drop-off) refreshes from fresh server data.
  const loadTracks = useCallback(async (resetSelection) => {
    try {
      const r = await axios.get(`${API}/quickpick/races?date=${selectedDate}`);
      if (r.data?.success) {
        setTracks(r.data.tracks || []);
        if (resetSelection && !pendingTriRef.current) {
          setSelectedTrack(""); setSelectedRace(""); setEntries([]); setResult(null); setRacePayouts([]);
        }
      }
    } catch { if (resetSelection) setTracks([]); }
  }, [selectedDate]);

  useEffect(() => { loadTracks(true); }, [loadTracks]);

  // Auto-refresh Up Next every 30s (finished races drop off, MTP recomputes)
  // without disturbing the loaded race/selection.
  useEffect(() => {
    const id = setInterval(() => loadTracks(false), 30000);
    return () => clearInterval(id);
  }, [loadTracks]);

  useEffect(() => {
    if (!selectedTrack || !selectedRace) { setEntries([]); setRacePayouts([]); return; }
    const key = `${selectedTrack}|${selectedRace}|${selectedDate}`;
    loadedKeyRef.current = null;   // pause autosave until this race's restore completes
    preFadeGridRef.current = {};    // reset per-runner pre-fade grid memory for the new race
    let cancelled = false;
    (async () => {
      setLoadingEntries(true); setResult(null);
      try {
        const r = await axios.get(`${API}/quickpick/entries?track=${selectedTrack}&race=${selectedRace}&date=${selectedDate}`);
        if (cancelled) return;
        const es = r.data?.entries || [];
        setEntries(es);
        setRacePayouts(r.data?.payouts || []);
        const fresh = {}, du = {}, bf = {}, wf = {}, ky = {};
        es.forEach((e) => { const pn = String(e.programNumber); fresh[pn] = new Set(); du[pn] = false; bf[pn] = 0; wf[pn] = 0; ky[pn] = false; });
        setGrid(fresh);
        setDontUse(du);
        setBestFin(bf); setWorstFin(wf); setKeySel(ky);
        setStraightBet({});
        // Restore default bet amounts — never carry a prior race's manual override.
        setPoolAmt({
          EX: String(poolDefaults.EX ?? bankroll ?? 50),
          TRI: String(poolDefaults.TRI ?? bankroll ?? 50),
          SPR: String(poolDefaults.SPR ?? bankroll ?? 50),
        });
        setStraightAmt({ WIN: "", WP: "" });
        // Auto-restore the user's saved inputs for this race (both modes; pref default ON).
        if (prefsRef.current?.rememberRaceInputs !== false) {
          try {
            const sr = await axios.get(`${API}/betmaker/race-inputs?track=${selectedTrack}&race=${selectedRace}&date=${selectedDate}`, { withCredentials: true });
            if (!cancelled) applySavedInputs(sr.data?.inputs, es);
          } catch { /* no saved inputs */ }
        }
      } catch { if (!cancelled) { setEntries([]); setRacePayouts([]); } }
      if (!cancelled) { loadedKeyRef.current = key; setLoadingEntries(false); }
    })();
    return () => { cancelled = true; };
  }, [selectedTrack, selectedRace, selectedDate]);

  // Adaptive silent auto-refresh of the runner grid (Strong/Fade signals + live
  // odds) for today's selected race. Merges fresh odds/signals into the existing
  // rows BY PROGRAM NUMBER (never replaces the array wholesale -> no scroll jump)
  // and never resets the user's grid, keys or dont-use. Guards against stale
  // responses so changing the race can't paint the previous race's data.
  const bmPostTimeRef = useRef(null);
  const bmSelectionRef = useRef("");
  useEffect(() => {
    const rs = selectedTrackData?.races || [];
    const raceObj = rs.find((r) => String(r.raceNumber) === String(selectedRace));
    bmPostTimeRef.current = raceObj?.postTime || null;
  }, [selectedTrackData, selectedRace]);

  useEffect(() => {
    const key = `${selectedTrack}|${selectedRace}|${selectedDate}`;
    bmSelectionRef.current = key;
    const isToday = selectedDate === formatDate(new Date());
    if (!(isToday && selectedTrack && selectedRace)) return;
    let timer = null;
    let cancelled = false;
    const cadence = () => {
      const pt = bmPostTimeRef.current;
      const mtp = pt ? calcMTP(pt, selectedDate) : null;
      if (mtp == null) return 60000;
      if (mtp <= 2) return 15000;
      if (mtp <= 5) return 30000;
      return 60000;
    };
    const tick = async () => {
      try {
        const r = await axios.get(
          `${API}/quickpick/entries?track=${selectedTrack}&race=${selectedRace}&date=${selectedDate}`
        );
        // Drop the response if the selection changed or the effect was torn down
        // while the request was in flight (prevents painting another race's data).
        if (cancelled || bmSelectionRef.current !== key) return;
        const fresh = r.data?.entries || [];
        const byPn = {};
        fresh.forEach((e) => { byPn[String(e.programNumber)] = e; });
        setEntries((prev) => {
          // Same runners? merge in place to keep row identity (no jump) AND
          // preserve the user's client-only MyOdds input (_myOdds lives on the
          // entry object, so a wholesale replace would wipe what they typed).
          if (prev.length && prev.every((e) => byPn[String(e.programNumber)])) {
            return prev.map((e) => {
              const f = byPn[String(e.programNumber)];
              return f ? { ...f, _myOdds: e._myOdds } : e;
            });
          }
          return fresh;
        });
        setRacePayouts(r.data?.payouts || []);
      } catch { /* keep prior grid on transient error */ }
      if (!cancelled) timer = setTimeout(tick, cadence());
    };
    timer = setTimeout(tick, cadence());
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [selectedTrack, selectedRace, selectedDate]);

  const togglePos = (pn, pos) => {
    setGrid((prev) => {
      const s = new Set(prev[pn] || []);
      s.has(pos) ? s.delete(pos) : s.add(pos);
      return { ...prev, [pn]: s };
    });
  };
  const toggleAll = (pn) => {
    setGrid((prev) => {
      const all = [1, 2, 3, 4];
      const isAll = all.every((p) => prev[pn]?.has(p));
      return { ...prev, [pn]: isAll ? new Set() : new Set(all) };
    });
  };
  // Header click: select (or clear) a finishing position for every active runner.
  const toggleColumn = (pos) => {
    setGrid((prev) => {
      const actives = entries.filter((e) => !e.scratched && !dontUse[String(e.programNumber)]);
      const allSet = actives.length > 0 && actives.every((e) => prev[String(e.programNumber)]?.has(pos));
      const next = { ...prev };
      actives.forEach((e) => {
        const pn = String(e.programNumber);
        const s = new Set(prev[pn] || []);
        allSet ? s.delete(pos) : s.add(pos);
        next[pn] = s;
      });
      return next;
    });
  };
  // Don't Use = definitive global exclude: clears the horse's picks and skips it
  // in every ALL / copy / carry-over action and in the generated tickets.
  const toggleDontUse = (pn) => {
    setDontUse((prev) => ({ ...prev, [pn]: !prev[pn] }));
    setGrid((prev) => ({ ...prev, [pn]: new Set() }));
    setKeySel((prev) => ({ ...prev, [pn]: false }));  // Don't Use clears any Key
    setStraightBet((prev) => { const n = { ...prev }; delete n[pn]; return n; });  // and any Win/WP
  };
  const clearAll = () => {
    setGrid((prev) => { const n = {}; Object.keys(prev).forEach((k) => (n[k] = new Set())); return n; });
    // Reset Best/Worst Finish only on NON-key runners; keep keyed runners as-is.
    setBestFin((prev) => { const n = { ...prev }; Object.keys(prev).forEach((k) => { if (!keySel[k]) n[k] = 0; }); return n; });
    setWorstFin((prev) => { const n = { ...prev }; Object.keys(prev).forEach((k) => { if (!keySel[k]) n[k] = 0; }); return n; });
    // Note: Key designations and Dont Use are intentionally preserved.
  };

  // Free Flow setters + shared position resolver (mode-aware).
  const setBest = (pn, v) => setBestFin((p) => ({ ...p, [pn]: v }));
  const setWorst = (pn, v) => setWorstFin((p) => ({ ...p, [pn]: v }));
  // Clickable green-dot shaping (Free Flow): toggle a runner's eligibility for a
  // finishing position, kept in sync with Best/Worst Finish. The allowed set stays
  // CONTIGUOUS (Best/Worst can only express a range), so toggling gap-fills to
  // [min..max]. Emptying it resets the runner to "Any". Never touches Dont Use/Keys.
  const toggleDot = (pn, pos) => {
    const cur = new Set(allowedFor(pn));
    if (cur.has(pos)) cur.delete(pos); else cur.add(pos);
    if (cur.size === 0) { setBest(pn, 0); setWorst(pn, 0); return; }
    const lo = Math.min(...cur), hi = Math.max(...cur);
    setBest(pn, lo > 1 ? lo : 0);
    setWorst(pn, hi < maxPositions ? hi : 0);
  };
  // ADW equivalent of "Exacta Fade -> use underneath": restrict the runner to the
  // 3rd (and lower) finishing positions; toggle off restores the grid to whatever it
  // was BEFORE the fade (empty by default) — never "all positions". Only meaningful
  // when a 3rd position exists (trifecta+); callers gate on maxPositions.
  const preFadeGridRef = useRef({});
  const setFadeUnderneathADW = (pn) => {
    const under = [3, 4].filter((p) => p <= maxPositions);
    setGrid((prev) => {
      const cur = prev[pn] || new Set();
      const active = under.length > 0 && under.every((p) => cur.has(p)) && ![1, 2].some((p) => cur.has(p)) && cur.size > 0;
      if (active) {
        const restored = preFadeGridRef.current[pn] || new Set();
        return { ...prev, [pn]: new Set(restored) };
      }
      preFadeGridRef.current[pn] = new Set(cur);
      return { ...prev, [pn]: new Set(under) };
    });
  };
  // Per-runner Win/WP pick (mutually exclusive). Amount comes from the value inputs.
  const toggleRunnerStraight = (pn, type) => {
    setStraightBet((prev) => {
      const n = { ...prev };
      if (n[pn] === type) delete n[pn]; else n[pn] = type;
      return n;
    });
  };
  const toggleKey = (pn) => {
    if (dontUse[pn]) return;
    setKeySel((p) => {
      const next = !p[pn];
      if (next) setBestFin((b) => ({ ...b, [pn]: 0 }));  // keying clears Best Finish -> Any
      return { ...p, [pn]: next };
    });
  };
  const resetBestAll = () => setBestFin((prev) => { const n = {}; Object.keys(prev).forEach((k) => (n[k] = 0)); return n; });
  const resetWorstAll = () => setWorstFin((prev) => { const n = {}; Object.keys(prev).forEach((k) => (n[k] = 0)); return n; });
  // Free Flow "Fill Last"/"Fill 2nd Last": set Best Finish = a position for every ACTIVE runner
  // that is currently UNCONSTRAINED (no Best/Worst set, not Don't Use, not a Key). One-shot apply.
  const fillBestUnconstrained = (pos) => {
    if (pos < 2) return;
    setBestFin((prev) => {
      const n = { ...prev };
      entries.forEach((e) => {
        const pn = String(e.programNumber);
        if (e.scratched) return;
        const constrained =
          (parseInt(prev[pn] || 0, 10) || 0) > 0 ||
          (parseInt(worstFin[pn] || 0, 10) || 0) > 0 ||
          !!dontUse[pn] || !!keySel[pn];
        if (!constrained) n[pn] = pos;
      });
      return n;
    });
  };
  // Allowed finishing positions for a runner in the CURRENT mode.
  const allowedFor = (pn) => {
    if (dontUse[pn]) return [];
    if (mode === "traditional") return Array.from(grid[pn] || []);
    const b0 = parseInt(bestFin[pn] || 0, 10) || 0;
    const w0 = parseInt(worstFin[pn] || 0, 10) || 0;
    const b = b0 >= 1 ? b0 : 1;
    const w = Math.min(w0 >= 1 ? w0 : maxPositions, maxPositions);
    const arr = [];
    for (let p = b; p <= w; p++) arr.push(p);
    return arr;
  };

  // ADW ">" : copy a column's selections into the NEXT column (additive — never removes existing).
  const copyToNext = (pos) => {
    if (pos + 1 > maxPositions) return;
    setGrid((prev) => {
      const next = { ...prev };
      entries.forEach((e) => {
        const pn = String(e.programNumber);
        if (e.scratched || dontUse[pn]) return;
        const s = new Set(prev[pn] || []);
        if (s.has(pos)) s.add(pos + 1);
        next[pn] = s;
      });
      return next;
    });
  };

  // ADW "SWAP" : exchange the selections of two columns.
  const swapCols = (a, b) => {
    setGrid((prev) => {
      const next = { ...prev };
      Object.keys(prev).forEach((pn) => {
        const s = new Set(prev[pn] || []);
        const hasA = s.has(a), hasB = s.has(b);
        hasB ? s.add(a) : s.delete(a);
        hasA ? s.add(b) : s.delete(b);
        next[pn] = s;
      });
      return next;
    });
  };

  // Pool-switch carry-over: when a new position column appears and is empty,
  // inherit the previous last column's picks (e.g. TRI->SU: 4th copies 3rd).
  const prevMax = useRef(maxPositions);
  useEffect(() => {
    const prev = prevMax.current;
    if (maxPositions > prev && entries.length) {
      setGrid((g) => {
        const next = { ...g };
        for (let c = prev + 1; c <= maxPositions; c++) {
          const colEmpty = entries.every((e) => !next[String(e.programNumber)]?.has(c));
          if (!colEmpty) continue;
          entries.forEach((e) => {
            const pn = String(e.programNumber);
            if (e.scratched || dontUse[pn]) return;
            const s = new Set(next[pn] || []);
            if (s.has(c - 1)) s.add(c);
            next[pn] = s;
          });
        }
        return next;
      });
    }
    prevMax.current = maxPositions;
  }, [maxPositions, entries, dontUse]);

  const fillPositions = (mode) => {
    let cols;
    if (mode === "last") cols = [maxPositions];
    else cols = maxPositions === 2 ? [2] : maxPositions === 3 ? [2, 3] : [3, 4];
    setGrid((prev) => {
      const next = { ...prev };
      entries.forEach((e) => {
        const pn = String(e.programNumber);
        if (e.scratched || dontUse[pn]) return;         // skip scratched / don't-use
        if ((next[pn]?.size || 0) > 0) return;          // skip already-configured
        next[pn] = new Set(cols);
      });
      return next;
    });
  };

  // Clear any prior tickets when the pool selection changes.
  useEffect(() => { setResult(null); }, [poolSel]);
  // Clear tickets when switching selection mode (semantics differ).
  useEffect(() => { setResult(null); }, [mode]);

  const activeEntries = entries.filter((e) => !e.scratched);
  // Short-field pool gating: a pool the track can't offer at this field size is
  // disabled and force-unchecked (<=3 => no EX; <=4 => no TRI/SPR).
  const fieldSize = activeEntries.length;
  useEffect(() => {
    if (!fieldSize) return;
    setPoolSel((prev) => {
      let changed = false;
      const next = { ...prev };
      POOLS.forEach((p) => {
        if (prev[p.code] && !poolOfferedAtField(p.code, fieldSize)) {
          next[p.code] = false;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [fieldSize]);
  const suppressStrong = activeEntries.filter((e) => e.mustUse).length > 2;
  // Fade (dont-use) suppression removed per owner (2026-07-23) — always show fade.
  const suppressFade = false;

  // Odds source always honours the user's pick; the engine falls back per-runner when proj is missing.
  const hasProj = activeEntries.some((e) => e.projOdds > 0);
  const pickOddsSource = (o) => { setOddsSource(o); try { localStorage.setItem("bm_odds_source", o); } catch {} };

  // Eligible runners per finishing position (active = not scratched / not Don't-Use), mode-aware.
  const eligible = useMemo(() => {
    const out = {};
    for (let pos = 1; pos <= 4; pos++) {
      out[pos] = activeEntries
        .filter((e) => allowedFor(String(e.programNumber)).includes(pos))
        .map((e) => String(e.programNumber));
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeEntries, grid, dontUse, mode, bestFin, worstFin, maxPositions]);

  // Keyed runners (Free Flow only): active, not Don't-Use, Key checked.
  const keyPns = useMemo(
    () => activeEntries.map((e) => String(e.programNumber)).filter((pn) => keySel[pn] && !dontUse[pn]),
    [activeEntries, keySel, dontUse]
  );
  const needsKey = mode === "freeflow" && keyPns.length === 0;

  // Walk all valid ordered combinations for a pool. In Free Flow every ticket must
  // contain at least one keyed runner, so combos without a key aren't counted — and
  // if a key is forced to occupy a given position on EVERY ticket, the other runners
  // are excluded from that position. Returns the combo count plus the TRUE
  // per-position occupancy (what can actually appear where).
  const walkCombos = (positions) => {
    const keys = mode === "freeflow" ? keyPns : [];
    const occ = {};
    for (let p = 1; p <= positions; p++) occ[p] = new Set();
    let count = 0;
    const combo = new Array(positions);
    const walk = (pos, used) => {
      if (pos > positions) {
        if (!keys.length || keys.some((k) => used.has(k))) {
          count += 1;
          for (let p = 1; p <= positions; p++) occ[p].add(combo[p - 1]);
        }
        return;
      }
      (eligible[pos] || []).forEach((pn) => { if (!used.has(pn)) { used.add(pn); combo[pos - 1] = pn; walk(pos + 1, used); used.delete(pn); } });
    };
    walk(1, new Set());
    return { count, occ };
  };
  const comboCount = (positions) => walkCombos(positions).count;

  const colPairs = useMemo(() => {
    const p = [];
    for (let a = 1; a <= maxPositions; a++) for (let b = a + 1; b <= maxPositions; b++) p.push([a, b]);
    return p;
  }, [maxPositions]);

  // Per-column rank coloring for the pool-score cells (legacy class logic).
  const dblColors = useMemo(() => computeRankColors(entries, "dblScore"), [entries]);
  const p3Colors = useMemo(() => computeRankColors(entries, "p3Score"), [entries]);
  const p4Colors = useMemo(() => computeRankColors(entries, "p4Score"), [entries]);
  const p5Colors = useMemo(() => computeRankColors(entries, "p5Score"), [entries]);
  const poolDropped = useMemo(() => ({
    dbl: entries.some((e) => e.dblUncovered),
    p3: entries.some((e) => e.p3Uncovered),
    p4: entries.some((e) => e.p4Uncovered),
    p5: entries.some((e) => e.p5Uncovered),
  }), [entries]);

  // Win Rank: active runners ranked by consensus (desc), competition ranking on
  // ties (two tied for 2nd -> both "2nd", next shown is "4th"). Color tier is keyed
  // to the RANK VALUE (not list position) so tied ranks ALWAYS share a color:
  // rank 1/2 = the two greens; the worst rank = red, the 2nd-worst DISTINCT rank =
  // orange; middle ranks uncolored (green wins on small-field overlap). Scratched /
  // no-consensus runners are omitted (blank).
  const winRank = useMemo(() => {
    const ranked = entries
      .filter((e) => !e.scratched && e.consensus != null)
      .map((e) => ({ pn: String(e.programNumber), c: e.consensus }))
      .sort((a, b) => b.c - a.c);
    const map = {};
    let rank = 0, prev = null;
    const rows = ranked.map((x, i) => {
      if (prev === null || x.c !== prev) { rank = i + 1; prev = x.c; }
      return { pn: x.pn, rank };
    });
    const distinct = [...new Set(rows.map((r) => r.rank))].sort((a, b) => b - a);
    const maxRank = distinct.length ? distinct[0] : null;
    const secondMaxRank = distinct.length > 1 ? distinct[1] : null;
    rows.forEach(({ pn, rank }) => {
      let tier = null;
      if (rank === 1) tier = "rank1";              // best (dark green)
      else if (rank === 2) tier = "rank2";         // 2nd (light green)
      if (!tier && rank === maxRank) tier = "n1";  // worst rank (red)
      else if (!tier && rank === secondMaxRank) tier = "n2"; // 2nd-worst rank (orange)
      map[pn] = { ord: ordinal(rank), tier };
    });
    return map;
  }, [entries]);

  const setMyOdds = (pn, val) => {
    setEntries((prev) => prev.map((e) => String(e.programNumber) === pn ? { ...e, _myOdds: val } : e));
  };

  // ── Per-race input memory ──────────────────────────────────────────────
  // Refs so the entries-load effect can read the latest mode/prefs without
  // re-subscribing, and gate autosave until a race's restore has completed.
  const modeRef = useRef(mode);
  const prefsRef = useRef(prefs);
  const loadedKeyRef = useRef(null);
  const pendingTriRef = useRef(null);
  const handleGenerateRef = useRef(null);
  const [triNudge, setTriNudge] = useState(0);
  useEffect(() => { modeRef.current = mode; }, [mode]);
  useEffect(() => { prefsRef.current = prefs; }, [prefs]);

  // Full snapshot of the user's shaping inputs (audit + restore) for BOTH modes:
  // free-flow (Best/Worst + Win/Place) and ADW (position grid). Sets are serialized
  // to arrays for JSON.
  const buildSnapshot = useCallback(() => {
    const myOdds = {};
    entries.forEach((e) => {
      const pn = String(e.programNumber);
      if (e._myOdds != null && e._myOdds !== "") myOdds[pn] = e._myOdds;
    });
    const truthy = (obj) => Object.fromEntries(Object.entries(obj || {}).filter(([, v]) => v));
    const gridSer = {};
    Object.entries(grid).forEach(([pn, set]) => { const arr = Array.from(set || []); if (arr.length) gridSer[pn] = arr; });
    return {
      mode,
      coverage: roundUp ? "full" : "exclude",
      oddsSource, blendPct,
      pools: Object.keys(poolSel).filter((k) => poolSel[k]),
      poolAmt, bankroll,
      myOdds,
      dontUse: truthy(dontUse),
      keySel: truthy(keySel),
      bestFin: truthy(bestFin),
      worstFin: truthy(worstFin),
      grid: gridSer,
      straightBet: { ...straightBet },
      straightAmt: { ...straightAmt },
    };
  }, [entries, mode, roundUp, oddsSource, blendPct, poolSel, poolAmt, bankroll, dontUse, keySel, bestFin, worstFin, grid, straightBet, straightAmt]);

  // Apply a saved snapshot, filtered to runners still in the field. Covers both
  // free-flow (Best/Worst/Win-Place) and ADW (position grid) inputs.
  const applySavedInputs = useCallback((snap, es) => {
    if (!snap) return;
    const pnSet = new Set(es.filter((e) => !e.scratched).map((e) => String(e.programNumber)));
    const pick = (obj) => Object.fromEntries(Object.entries(obj || {}).filter(([k]) => pnSet.has(k)));
    const mo = pick(snap.myOdds);
    if (Object.keys(mo).length) {
      setEntries((prev) => prev.map((e) => { const pn = String(e.programNumber); return mo[pn] != null ? { ...e, _myOdds: mo[pn] } : e; }));
    }
    setDontUse((prev) => ({ ...prev, ...pick(snap.dontUse) }));
    setKeySel((prev) => ({ ...prev, ...pick(snap.keySel) }));
    setBestFin((prev) => ({ ...prev, ...pick(snap.bestFin) }));
    setWorstFin((prev) => ({ ...prev, ...pick(snap.worstFin) }));
    const g = pick(snap.grid);
    if (Object.keys(g).length) {
      setGrid((prev) => { const n = { ...prev }; Object.entries(g).forEach(([pn, arr]) => { n[pn] = new Set(arr); }); return n; });
    }
    const sb = pick(snap.straightBet);
    if (Object.keys(sb).length) setStraightBet(sb);
    if (snap.straightAmt) setStraightAmt((prev) => ({ ...prev, ...snap.straightAmt }));
  }, []);

  // Debounced silent auto-save of the current race's inputs (both modes).
  // Gated on loadedKeyRef so it never fires mid-load/restore and clobbers saved data.
  useEffect(() => {
    if (!selectedTrack || !selectedRace || !entries.length) return;
    const key = `${selectedTrack}|${selectedRace}|${selectedDate}`;
    if (loadedKeyRef.current !== key) return;
    const t = setTimeout(() => {
      axios.put(`${API}/betmaker/race-inputs`, {
        track: selectedTrack, race: parseInt(selectedRace, 10), date: selectedDate,
        inputs: buildSnapshot(),
      }, { withCredentials: true }).catch(() => {});
    }, 800);
    return () => clearTimeout(t);
  }, [buildSnapshot, mode, selectedTrack, selectedRace, selectedDate, entries, dontUse, keySel, bestFin, worstFin, grid, straightBet, straightAmt, roundUp, oddsSource, blendPct, poolSel, poolAmt, bankroll]);


  const handleGenerate = async (topUpArg) => {
    const topUpPools = Array.isArray(topUpArg) ? topUpArg : [];
    setError(""); setResult(null);
    if (!selectedTrack || !selectedRace) { setError("Select a track and race."); return; }
    const hasStraight = winRunners.length > 0 || wpRunners.length > 0;
    if (selectedPools.length === 0 && !hasStraight) { setError("Select a pool, or check Win/WP on a runner."); return; }
    // Exotic pools (freeflow) require at least one Key. Straight-only wagers do not.
    if (mode === "freeflow" && selectedPools.length > 0 && keyPns.length === 0) {
      setError("Select at least one Key horse — every ticket must include at least one key.");
      return;
    }
    // Snapshot the per-runner Win/WP straight wagers so they render alongside exotics.
    const straightSnapshot = () => {
      const winAmt = resolvedStraightAmt("WIN"), wpAmt = resolvedStraightAmt("WP");
      return {
        win: winRunners.map((pn) => ({ pn, amount: winAmt })),
        wp: wpRunners.map((pn) => ({ pn, amount: wpAmt })),
      };
    };
    // Straight-only (Win/WP, no vertical pool): no backend combo generation needed.
    if (selectedPools.length === 0) {
      setResult({ pools: [], totalWager: winStake + wpStake, straight: straightSnapshot(), payouts: racePayouts });
      return;
    }
    const payloadEntries = entries.map((e) => {
      const pn = String(e.programNumber);
      return {
        programNumber: pn,
        myOdds: e._myOdds != null && e._myOdds !== "" ? parseFloat(e._myOdds) : null,
        allowedPositions: allowedFor(pn),
        mustUse: mode === "freeflow" ? !!keySel[pn] && !dontUse[pn] : false,
        scratched: !!e.scratched,
      };
    });
    setGenerating(true);
    try {
      const betAmounts = {};
      selectedPools.forEach((p) => { betAmounts[p.code] = resolvedAmt(p.code); });
      const r = await axios.post(`${API}/betmaker/generate`, {
        track: selectedTrack, raceNumber: parseInt(selectedRace, 10), raceDate: selectedDate,
        pools: selectedPools.map((p) => p.code), bankroll: parseFloat(bankroll) || 0,
        betAmounts, oddsSource, blendPct, roundUp, topUpPools, entries: payloadEntries,
      }, { withCredentials: true });
      setResult({ ...r.data, totalWager: (r.data.totalWager || 0) + winStake + wpStake, straight: straightSnapshot() });
      setToppedUp(topUpPools);
      // Remember-last-used: quietly persist the current config as defaults.
      if (prefs.rememberLastUsed) {
        const snapshot = {
          defaultMode: mode, bankroll: parseFloat(bankroll) || 0,
          poolDefaults: betAmounts,
          pools: selectedPools.map((p) => p.code), oddsSource, blendPct, roundUp,
        };
        axios.put(`${API}/betmaker/preferences`, snapshot, { withCredentials: true })
          .then((res) => setPrefs((prev) => ({ ...prev, ...(res.data?.preferences || {}) })))
          .catch(() => {});
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to generate tickets.");
    }
    setGenerating(false);
  };

  // Keep a live ref to handleGenerate so a deferred Opt-In build uses fresh state.
  handleGenerateRef.current = handleGenerate;

  // Apply a detected Strong-key Trifecta play onto the grid, then auto-build.
  const applyTriPlay = useCallback((spec) => {
    if (!spec || !entries.length) return;
    const ky = {}, bf = {}, wf = {}, du = {};
    entries.forEach((e) => { const pn = String(e.programNumber); ky[pn] = false; bf[pn] = 0; wf[pn] = 0; du[pn] = false; });
    let toastMsg;
    if (spec.variant === "nostrong") {
      // Keys Only: top-2 consensus keys fill {1,2,3}; other non-fades {2,3}; fades 3rd-only.
      // Mark the consensus keys as grid keys (≥1-key semantics) so the "needs a key"
      // guard is satisfied — pos1 is already restricted to keys, so combos are unchanged.
      (spec.keys || []).forEach((pn) => { ky[String(pn)] = true; bf[String(pn)] = 1; wf[String(pn)] = 3; });
      (spec.others || []).forEach((pn) => { bf[String(pn)] = 2; wf[String(pn)] = 3; });
      (spec.fade3 || []).forEach((pn) => { bf[String(pn)] = 3; wf[String(pn)] = 3; });
      (spec.excluded || []).forEach((pn) => { du[String(pn)] = true; });
      toastMsg = `+ROI Trifecta loaded — keys #${(spec.keys || []).join(", #")}`;
    } else {
      const S = String(spec.strong);
      ky[S] = true; bf[S] = 1; wf[S] = spec.worstFin || 3;
      (spec.fade3 || []).forEach((pn) => { bf[String(pn)] = 3; wf[String(pn)] = 3; });
      (spec.excluded || []).forEach((pn) => { du[String(pn)] = true; });
      toastMsg = `+ROI Trifecta loaded — keying #${S}`;
    }
    setMode("freeflow");
    setKeySel(ky); setBestFin(bf); setWorstFin(wf); setDontUse(du);
    setPoolSel({ EX: false, TRI: true, SPR: false });
    setBankroll(String(spec.bankroll));
    setPoolAmt((a) => ({ ...a, TRI: String(spec.bankroll) }));
    setRoundUp(spec.version === "V1");
    const topUp = spec.version === "V3" ? ["TRI"] : [];
    toast.success(toastMsg);
    setTimeout(() => { if (handleGenerateRef.current) handleGenerateRef.current(topUp); }, 180);
  }, [entries]);

  // On mount: a cross-app Opt-In handoff from IntelliTix lands here via sessionStorage.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("triPlayOptIn");
      if (!raw) return;
      sessionStorage.removeItem("triPlayOptIn");
      const d = JSON.parse(raw);
      if (!d?.track || d.race == null || !d.date) return;
      pendingTriRef.current = { date: d.date, track: d.track, race: String(d.race), spec: d.spec, stage: "need-select", key: `${d.track}|${d.race}|${d.date}` };
      setSelectedDate(d.date);
      setTriNudge((n) => n + 1);
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Opt-In application: ensure the target race is selected (re-asserting if the
  // date-change track reload wipes it), then apply the play once its entries load.
  useEffect(() => {
    const p = pendingTriRef.current;
    if (!p || p.done) return;
    if (selectedDate !== p.date) return;
    const matches = selectedTrack === p.track && String(selectedRace) === String(p.race);
    if (!matches) { setSelectedTrack(p.track); setSelectedRace(String(p.race)); return; }
    if (loadingEntries || loadedKeyRef.current !== p.key || !entries.length) return;
    applyTriPlay(p.spec);
    p.done = true;
    // Keep the ref alive briefly so the mount/date-change track reload can't wipe
    // the just-applied selection (its reset is guarded while a handoff is pending).
    setTimeout(() => { if (pendingTriRef.current === p) pendingTriRef.current = null; }, 2500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tracks, selectedDate, selectedTrack, selectedRace, loadingEntries, entries, triNudge, applyTriPlay]);

  // Recalculate tickets live when the Ticket Coverage mode changes (only if tickets
  // are already shown) so the ticket count/total updates without re-clicking Create.
  const coverageMounted = useRef(false);
  useEffect(() => {
    if (!coverageMounted.current) { coverageMounted.current = true; return; }
    if (result) handleGenerate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roundUp]);

  const doExport = (kind) => {
    const hasStraight = winRunners.length > 0 || wpRunners.length > 0;
    if (!result?.pools && !hasStraight) return;
    const code = kind === "amwager" ? (selectedTrackData?.amcode || selectedTrack) : (selectedTrackData?.xbcode || selectedTrack);
    const builder = kind === "amwager" ? amwagerLines : xpressLines;
    const exoticContent = (result?.pools || []).filter((p) => p.success && p.tickets?.length)
      .map((p) => {
        const wheel = shouldWheel(p.tickets?.length, consolThresholds[p.pool], p.poolMinimum, p.ticketMin);
        return builder(code, selectedRace, p.pool, p.tickets, selectedDate, wheel, p.poolMinimum, p.ticketMin);
      });
    // Straight wagers: WIN, and WP = separate WIN + PLC (AmWager) / WIN + PLACE (XpressBet) legs.
    const d = selectedDate.replace(/-/g, "");
    const pre = code.slice(0, -1), suf = code.slice(-1);
    const winAmt = resolvedStraightAmt("WIN"), wpAmt = resolvedStraightAmt("WP");
    const amLine = (bt, pn, amt) => `,,${d},USA,${pre},${suf},${bt},,${selectedRace},${pn},USD,${amt.toFixed(2)},,,`;
    const xbLine = (bt, pn, amt) => `${selectedDate},${code},${selectedRace},${bt},${pn},${amt.toFixed(2)},,,WHEEL`;
    const sl = [];
    winRunners.forEach((pn) => sl.push(kind === "amwager" ? amLine("WIN", pn, winAmt) : xbLine("WIN", pn, winAmt)));
    wpRunners.forEach((pn) => {
      sl.push(kind === "amwager" ? amLine("WIN", pn, wpAmt) : xbLine("WIN", pn, wpAmt));
      sl.push(kind === "amwager" ? amLine("PLC", pn, wpAmt) : xbLine("PLACE", pn, wpAmt));
    });
    const content = [...sl, ...exoticContent].filter(Boolean).join("\n");
    if (!content) return;
    downloadFile(content, `${kind}_betmaker_${selectedDate}_${code}_r${selectedRace}.txt`);
    // Record the download for the Admin Downloads report (one row per pool).
    (result?.pools || []).filter((p) => p.success && p.tickets?.length).forEach((p) => {
      axios.post(`${API}/quickpick/log-download`, {
        track: selectedTrack,
        race_number: parseInt(selectedRace, 10),
        race_date: selectedDate,
        pool: p.pool,
        format: kind,
        ticket_count: p.ticketCount,
        total_wager: p.totalWager,
        tickets: p.tickets.map((t) => ({ pool: p.pool, combo: t.combo, amount: t.amount })),
        inputs: buildSnapshot(),
      }, { withCredentials: true }).catch(() => {});
    });
  };

  const races = selectedTrackData?.races || [];
  const raceNums = races.map((r) => r.raceNumber);
  const curIdx = raceNums.indexOf(parseInt(selectedRace, 10));
  const canStep = (dir) => curIdx >= 0 && curIdx + dir >= 0 && curIdx + dir < raceNums.length;
  const stepRace = (dir) => { if (canStep(dir)) setSelectedRace(String(raceNums[curIdx + dir])); };

  // UP NEXT: each track's soonest non-final race, soonest post time first.
  // Sort on the FULL timestamp (epoch), not time-of-day — otherwise a next-day /
  // overnight foreign card (e.g. HKS at 04:30 the following morning) collapses to a
  // tiny minutes-of-day key and wrongly sorts to the top.
  const postSortKey = (pt) => {
    if (!pt) return Infinity;
    const t = Date.parse(pt);
    return isNaN(t) ? Infinity : t;
  };
  const upNext = tracks
    .map((t) => {
      const rs = t.races || [];
      // Admin Live-Ops override wins: finished -> drop; next_race -> force that race.
      if (t.adminFinished) return null;
      if (t.adminNextRace != null) {
        const r = rs.find((x) => Number(x.raceNumber) === Number(t.adminNextRace) && x.status !== "CANCELLED" && !x.cancelled);
        return r ? { code: t.code, name: t.name, race: r.raceNumber, postTime: r.postTime } : null;
      }
      // Next playable race: first that is neither official (status FINAL) nor
      // cancelled. Gated on official status — not the `final` boolean, which the
      // feed can flip early at POST. No fallback to R1 — finished/cancelled-only
      // cards drop off entirely. ALSO skip a race whose results file never arrived
      // (stuck at POST) when a LATER race on the card is already FINAL — a later
      // official race proves this one ran (feed gap on its results).
      const laterFinal = (i) => rs.slice(i + 1).some((r) => r.status === "FINAL");
      // Unstick a stuck race when the NEXT sequential (non-cancelled) race has a feed
      // cycle at <=18 MTP (mtp is null until it actually cycles; scheduled post times
      // are approximate and are NOT used).
      const nextCycling = (i) => {
        const nxt = rs.slice(i + 1).find((r) => r.status !== "CANCELLED" && !r.cancelled);
        return !!nxt && nxt.mtp != null && nxt.mtp <= 18;
      };
      const next = rs.find((r, i) => r.status !== "FINAL" && r.status !== "CANCELLED" && !r.cancelled && !laterFinal(i) && !nextCycling(i));
      return next ? { code: t.code, name: t.name, race: next.raceNumber, postTime: next.postTime } : null;
    })
    .filter(Boolean)
    .sort((a, b) => postSortKey(a.postTime) - postSortKey(b.postTime))
    .slice(0, 16);

  const pickUpNext = (u) => { setSelectedTrack(u.code); setSelectedRace(String(u.race)); };

  // Strong-key "+ROI Trifecta Play" detection (whole card: live + final races).
  const triDay = useTriPlaysDay(selectedDate);
  const triPlays = triDay.byKey;
  const optInTriInPage = (u, spec) => {
    const key = `${u.code}|${u.race}|${selectedDate}`;
    pendingTriRef.current = { date: selectedDate, track: u.code, race: String(u.race), spec, stage: "need-select", key };
    setSelectedTrack(u.code); setSelectedRace(String(u.race));
    setTriNudge((n) => n + 1);
  };

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900 px-4 pt-3 pb-4" data-bm-theme={prefs.theme || "bright"} data-testid="betmaker-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-3">
          <Sparkles className="w-6 h-6 text-amber-500" />
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">IntelliTix<sup>Pro</sup> - BetMaker</h1>
            <p className="text-sm text-slate-500">Your Opinion - Our Math</p>
          </div>
        </div>
        <div className="hidden md:flex flex-1 justify-center">
          <TrialBadge theme="light" />
        </div>
        <div className="flex items-center gap-3">
          <Link to="/" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-sm shadow-md shadow-amber-500/30 border border-amber-400/40 transition-transform hover:scale-105" data-testid="nav-quickpick" title="Switch to IntelliTix"><ArrowLeftRight className="w-4 h-4" /><span>IntelliTix</span></Link>
          <button
            onClick={() => window.open('https://youtu.be/7QMp4FB6ru0', '_blank', 'noopener')}
            className="inline-flex items-center gap-1.5 text-sm text-amber-600 hover:text-amber-500 transition font-semibold"
            data-testid="betmaker-video-link"
            title="Watch the BetMaker overview video"
          >
            <PlayCircle className="w-4 h-4" />
            <span>Watch overview</span>
          </button>
          <button
            onClick={() => window.open('/betmaker-guide.html', '_blank', 'noopener')}
            className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-sm shadow-md shadow-amber-500/30 border border-amber-400/40 transition-transform hover:scale-105"
            data-testid="betmaker-guide-btn"
            title="New here? Open the BetMaker User Guide"
          >
            <BookOpen className="w-4 h-4" />
            <span>IntelliTix<sup>Pro</sup> Guide</span>
          </button>
          <button
            onClick={() => window.open('/file-upload-guide.html', '_blank', 'noopener')}
            className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm shadow-md shadow-emerald-600/30 border border-emerald-500/40 transition-transform hover:scale-105"
            data-testid="file-upload-guide-btn"
            title="How to upload your tickets to your ADW"
          >
            <Upload className="w-4 h-4" />
            <span>File Upload Guide</span>
          </button>
          <button
            onClick={toggleTheme}
            className="flex items-center justify-center w-9 h-9 rounded-full bg-slate-200 hover:bg-slate-300 text-slate-700 transition-colors"
            data-testid="betmaker-theme-toggle"
            title={prefs.theme === "soft" ? "Switch to bright background" : "Soften the background"}
          >
            {prefs.theme === "soft" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <AppMenu user={user} onLogout={logout} showPreferences onOpenPreferences={() => setPrefsOpen(true)} theme="light" />
        </div>
      </div>
      <Toaster />
      <BetMakerPreferences open={prefsOpen} onOpenChange={setPrefsOpen} prefs={prefs} betmakerEnabled={betmakerEnabled} onSave={savePrefs} />

      {/* Collapsible Guide (legacy port) — hidden until copy is reviewed (GUIDE_ENABLED). */}
      {GUIDE_ENABLED && (
      <div className="mb-4">
        <button onClick={() => setShowGuide((v) => !v)} data-testid="guide-toggle"
          className="flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-slate-900">
          <Info className="w-4 h-4 text-emerald-600" />
          {showGuide ? "Hide Guide" : "Show Guide"}
          {showGuide ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        {showGuide && (
          <Card className="bg-white border-slate-200 shadow-sm mt-2" data-testid="guide-panel">
            <CardContent className="p-4 space-y-3">
              {(GUIDE_BY_MODE[mode] || []).map(([term, desc]) => (
                <div key={term} className="grid grid-cols-1 md:grid-cols-[170px_1fr] gap-1 md:gap-4 text-sm">
                  <div className="font-semibold text-emerald-700">{term}</div>
                  <div className="text-slate-600">{desc}</div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
      )}

      {/* Controls */}
      <Card className="bg-white border-slate-200 shadow-sm mb-4">
        <CardContent className="p-4 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {/* Date / Track / Race */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <label className="text-xs text-slate-500 uppercase tracking-wide">Date</label>
              <Input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)}
                className="bg-white border-slate-300 text-slate-900 w-36 h-9" data-testid="date-picker" />
            </div>
            <Select value={selectedTrack} onValueChange={(v) => {
              setSelectedTrack(v);
              const t = tracks.find((x) => x.code === v);
              const rs = t?.races || [];
              const live = (r) => !r.cancelled && r.status !== "CANCELLED";
              // Skip a stuck-at-POST race whose results never arrived when a later
              // race on the card is already FINAL (that race being official proves
              // this one ran). Gate on official status, not the `final` boolean.
              const laterFinal = (i) => rs.slice(i + 1).some((r) => r.status === "FINAL");
              const nextCycling = (i) => {
                const nxt = rs.slice(i + 1).find((r) => r.status !== "CANCELLED" && !r.cancelled);
                return !!nxt && nxt.mtp != null && nxt.mtp <= 18;
              };
              const cur = rs.find((r, i) => r.status !== "FINAL" && live(r) && !laterFinal(i) && !nextCycling(i)) || rs.find(live) || rs[0];
              setSelectedRace(cur ? String(cur.raceNumber) : "");
            }}>
              <SelectTrigger className="bg-white border-slate-300 text-slate-900" data-testid="track-select"><SelectValue placeholder="Track" /></SelectTrigger>
              <SelectContent className="bg-white border-slate-200 text-slate-900 max-h-72">
                {tracks.map((t) => <SelectItem key={t.code} value={t.code} data-testid={`track-option-${t.code}`}>{t.code} — {t.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="icon" className="h-9 w-9 border-slate-300 text-slate-700 shrink-0" onClick={() => stepRace(-1)} disabled={!canStep(-1)} data-testid="race-prev"><ChevronLeft className="w-4 h-4" /></Button>
              <Select value={selectedRace} onValueChange={setSelectedRace}>
                <SelectTrigger className="bg-white border-slate-300 text-slate-900 flex-1" data-testid="race-select"><SelectValue placeholder="Race" /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200 text-slate-900 max-h-72">
                  {races.map((r) => {
                    const isCancelled = r.cancelled || r.status === "CANCELLED";
                    return (
                    <SelectItem key={r.raceNumber} value={String(r.raceNumber)} disabled={isCancelled} data-testid={`race-option-${r.raceNumber}`}
                      className={isCancelled ? "text-slate-400 line-through opacity-60" : ""}>
                      <span className="flex items-center gap-2">
                        R{r.raceNumber}{fmtPost(r.postTime) ? ` - ${fmtPost(r.postTime)}` : ""}
                        {triDay.byKey[`${selectedTrack}|${r.raceNumber}`] && (
                          <span className="text-[10px] font-extrabold tracking-wide px-1.5 py-0.5 rounded bg-fuchsia-600 text-white" data-testid={`race-tri-tag-${r.raceNumber}`}>+TRI</span>
                        )}
                        {r.isQH && <span className="text-[10px] font-bold px-1 rounded bg-orange-100 text-orange-700">QH</span>}
                        {r.isForeign && <span className="text-[10px] font-bold px-1 rounded bg-sky-100 text-sky-700" data-testid={`race-sim-${r.raceNumber}`}>SIM</span>}
                        {isCancelled ? <span className="text-[10px] font-semibold px-1 rounded bg-red-100 text-red-700 no-underline" data-testid={`race-cancelled-${r.raceNumber}`}>CANCELLED</span>
                          : r.final && <span className="text-[10px] font-semibold px-1 rounded bg-amber-100 text-amber-700">OFFICIAL</span>}
                      </span>
                    </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <Button variant="outline" size="icon" className="h-9 w-9 border-slate-300 text-slate-700 shrink-0" onClick={() => stepRace(1)} disabled={!canStep(1)} data-testid="race-next"><ChevronRight className="w-4 h-4" /></Button>
            </div>
            {(() => {
              const tri = triDay.byKey[`${selectedTrack}|${selectedRace}`];
              if (!tri) return null;
              return (
                <div className="w-full flex items-center justify-between gap-2 rounded-md border-2 border-fuchsia-400 bg-fuchsia-50 px-3 py-1.5 shadow-sm" data-testid="tri-race-banner">
                  <div className="flex items-center gap-1.5 text-fuchsia-700 min-w-0">
                    <Zap className="w-4 h-4 shrink-0" />
                    <span className="text-xs font-bold truncate">+ROI Trifecta</span>
                  </div>
                  <button onClick={() => optInTriInPage({ code: selectedTrack, race: parseInt(selectedRace) }, tri)}
                    className="shrink-0 px-2.5 py-1 rounded text-[11px] font-bold bg-fuchsia-600 text-white hover:bg-fuchsia-500 transition" data-testid="tri-race-optin-btn">
                    Build
                  </button>
                </div>
              );
            })()}
          </div>

          {/* Pools + per-pool bet amounts (Vertical / MultiV) */}
          <div className="space-y-2">
            <label className="text-xs text-slate-500 uppercase tracking-wide">
              Pools &amp; Bet Amount <span className="normal-case tracking-normal italic text-slate-400" data-testid="horizontals-coming-soon">(Horizontals coming soon)</span>
            </label>
            <div className="grid grid-cols-[auto_auto] gap-x-8 gap-y-2 w-fit items-center">
              {renderPoolCell("EX")}
              {renderStraightCell("WIN", "Win")}
              {renderPoolCell("TRI")}
              {renderStraightCell("WP", "WP")}
              {renderPoolCell("SPR")}
              <div />
            </div>
            {poolOfferedAtField("EX", fieldSize) && (
              <p className="text-[11px] text-slate-500 italic pl-1" data-testid="ex-single-min-note">
                *Min stake for a single EX combination is $2
              </p>
            )}
            {HORIZONTALS_ENABLED && HORIZONTAL_POOLS.map((p) => (
              <div key={p.code} className="flex items-center gap-2 text-sm opacity-50" data-testid={`pool-row-${p.code}`}>
                <label className="flex items-center gap-2 text-slate-500 w-24 shrink-0">
                  <Checkbox disabled />
                  {p.name}
                </label>
                <div className="relative">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-300">$</span>
                  <Input type="number" disabled className="w-16 pl-5 text-right bg-slate-50" />
                </div>
              </div>
            ))}

            {/* Preset chips (left) + shaded Total Stake (right) on one row */}
            <div className="flex flex-wrap items-center gap-1 pt-1">
              <span className="text-[11px] text-slate-400 mr-1">
                {focusedPool && (poolSel[focusedPool] || focusedPool === "WIN" || focusedPool === "WP") ? `Set Bet Amt:` : "Presets:"}
              </span>
              {BANKROLL_PRESETS.map((b) => {
                const straightFocus = focusedPool === "WIN" || focusedPool === "WP";
                const tgt = focusedPool && (poolSel[focusedPool] || straightFocus) ? focusedPool
                  : (selectedPools[0] && selectedPools[0].code);
                const isStraight = tgt === "WIN" || tgt === "WP";
                const cur = isStraight ? straightAmt[tgt] : (tgt ? poolAmt[tgt] : undefined);
                const active = tgt && String(b) === String(cur);
                return (
                  <button key={b} disabled={!tgt}
                    onClick={() => {
                      if (!tgt) return;
                      if (isStraight) setStraightAmt((a) => ({ ...a, [tgt]: String(b) }));
                      else setPoolAmt((a) => ({ ...a, [tgt]: String(b) }));
                    }}
                    className={`px-2 py-0.5 text-xs rounded border transition-colors ${active ? "bg-amber-500 text-white border-amber-500" : "border-slate-300 text-slate-700 hover:border-slate-400"} disabled:opacity-40 disabled:cursor-not-allowed`}
                    data-testid={`bankroll-preset-${b}`}>{b}</button>
                );
              })}
              {(selectedPools.length > 0 || winStake > 0 || wpStake > 0) && (
                <span className="ml-auto rounded-md bg-slate-100 ring-1 ring-slate-200 px-2.5 py-1 text-xs font-bold text-slate-900 whitespace-nowrap" data-testid="betmaker-total-stake">
                  Total Stake: ${Math.round(totalStake)}
                </span>
              )}
            </div>
            {(winRunners.length > 0 || wpRunners.length > 0) && (
              <div className="text-[11px] text-slate-600 space-y-0.5 pt-1" data-testid="straight-wager-summary">
                {winRunners.length > 0 && (
                  <div data-testid="straight-summary-win"><span className="font-semibold text-blue-700">WIN</span> @ ${resolvedStraightAmt("WIN")} ea: #{winRunners.join(", #")}</div>
                )}
                {wpRunners.length > 0 && (
                  <div data-testid="straight-summary-wp"><span className="font-semibold text-purple-700">WP</span> (Win + Place, each ${resolvedStraightAmt("WP")}): #{wpRunners.join(", #")}</div>
                )}
              </div>
            )}
          </div>

          {/* Blender (disabled — kept behind flag) + Odds source */}
          <div className="space-y-2">
            {BLENDER_ENABLED ? (
              <>
                <label className="text-xs text-slate-500 uppercase tracking-wide">Blender</label>
                <input type="range" min="0" max="100" value={blendPct} onChange={(e) => setBlendPct(parseInt(e.target.value, 10))}
                  className="w-full h-2 rounded-full appearance-none cursor-pointer"
                  style={{ background: "linear-gradient(90deg,#2563eb,#22c55e,#f59e0b,#ef4444)" }}
                  data-testid="blender-slider" />
                <div className="flex justify-between text-[10px] text-slate-500">
                  <span>Weight Odds</span><span className="text-slate-900 font-semibold">{blendPct}%</span><span>Spread Evenly</span>
                </div>
              </>
            ) : (
              <label className="text-xs text-slate-500 uppercase tracking-wide">Odds Source</label>
            )}
            <div className="flex items-center gap-2 pt-1">
              <span className="text-xs text-slate-500">Use:</span>
              {["proj", "live"].map((o) => (
                <button key={o} onClick={() => pickOddsSource(o)}
                  className={`px-3 py-1 text-xs rounded-full ${oddsSource === o ? "bg-amber-500 text-white font-semibold" : "bg-slate-100 text-slate-700"}`}
                  data-testid={`odds-${o}`}>{o === "proj" ? "Projected" : "Live"}</button>
              ))}
              {entries.length > 0 && !hasProj && oddsSource === "proj" && (
                <span className="text-[10px] text-amber-600 font-medium" data-testid="no-proj-hint">no proj this race → priced off Live</span>
              )}
            </div>
            {/* Ticket coverage mode toggle */}
            <div className="pt-3 mt-2 border-t border-slate-100">
              <label className="text-xs text-slate-500 uppercase tracking-wide inline-flex items-center gap-1.5">
                Ticket Coverage
                <TooltipProvider delayDuration={150}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span data-testid="coverage-info" className="inline-flex cursor-help"><Info className="w-3.5 h-3.5 text-slate-400 hover:text-slate-600" /></span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs text-xs leading-relaxed">
                      <p className="font-semibold mb-1">Ticket coverage</p>
                      <b>Full Ticket Coverage:</b> every valid combination is played and your bankroll is funded to match your bet amount exactly. The total only exceeds your bet amount when there are too many combinations to cover them all at the pool minimum.
                      <span className="block mt-1.5"><b>Exclude Low Probability Tickets:</b> any combination whose fair (probability-weighted) stake is below the pool minimum is dropped — not padded up to the minimum — and your bankroll is spread across the rest so the total matches your bet amount exactly.</span>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </label>
              <div className="flex flex-col items-start gap-1.5 pt-1.5" data-testid="coverage-toggle">
                <button onClick={() => setRoundUp(true)} data-testid="coverage-full"
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-md transition-colors ${roundUp ? "bg-amber-500 text-white font-bold" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>
                  {roundUp && <Check className="w-3.5 h-3.5" />}
                  Full Ticket Coverage
                </button>
                <button onClick={() => setRoundUp(false)} data-testid="coverage-exclude"
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-md transition-colors ${!roundUp ? "bg-amber-500 text-white font-bold" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>
                  {!roundUp && <Check className="w-3.5 h-3.5" />}
                  Exclude Low Probability Tickets
                </button>
              </div>
              <p className="text-xs text-slate-600 mt-1.5 leading-snug" data-testid="coverage-hint">
                {roundUp
                  ? "Every combination is covered and funded to match your bet amount; the total only exceeds your bet when there are too many combos to cover at the minimum."
                  : "Any combination whose fair (probability-weighted) stake is below the pool minimum is dropped; the rest are funded to match your bet amount exactly."}
              </p>
            </div>
            {/* "Create Tickets" action lives in the Runners header (centered). */}
          </div>
        </CardContent>
      </Card>

      {error && (
        <div className="mb-3 flex items-center gap-2 rounded-md border-2 border-red-300 bg-red-50 px-4 py-3 text-red-700 font-semibold shadow-sm" data-testid="betmaker-error" role="alert">
          <AlertTriangle className="w-5 h-5 shrink-0 text-red-500" />
          <span>{error}</span>
        </div>
      )}


      {/* Up Next */}
      {upNext.length > 0 && (
        <Card className="bg-white border-slate-200 shadow-sm mb-3" data-testid="upnext-section">
          <CardContent className="p-2">
            <div className="flex items-center gap-3">
              <span className="text-sm font-semibold text-slate-900 shrink-0">Up Next</span>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {upNext.map((u) => {
                  const active = u.code === selectedTrack && String(u.race) === selectedRace;
                  const tri = triPlays[`${u.code}|${u.race}`];
                  return (
                    <button key={`${u.code}-${u.race}`} type="button"
                      onClick={() => (tri ? optInTriInPage(u, tri) : pickUpNext(u))}
                      title={tri ? `${u.name} — +ROI Trifecta · opt-in to build` : u.name}
                      className={`relative shrink-0 rounded-lg border px-3 py-1 text-left transition-colors ${tri ? "border-fuchsia-400 bg-fuchsia-50 ring-2 ring-fuchsia-300 animate-pulse" : active ? "border-amber-400 bg-amber-50" : "border-slate-200 bg-slate-50 hover:bg-slate-100"}`}
                      data-testid={`upnext-${u.code}-${u.race}`}>
                      <div className="text-sm font-bold text-slate-900">{u.code} · R{u.race}</div>
                      {(() => {
                        const m = fmtMTP(u.postTime, selectedDate);
                        return <div className={`text-xs font-semibold ${m.urgent ? "text-red-600" : "text-emerald-600"}`}>{m.text || "—"}</div>;
                      })()}
                      {tri && (
                        <div className="mt-0.5 flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-wide text-fuchsia-700" data-testid={`triplay-${u.code}-${u.race}`}>
                          <Zap className="w-3 h-3" /> +ROI Trifecta
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Grid */}
      <Card className="bg-white border-slate-200 shadow-sm mb-4">
        <CardHeader className="py-3">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-3 min-h-[36px]">
            {/* Group 1: Mode selector */}
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Mode</span>
              <div className="flex items-center gap-1 rounded-full bg-slate-100 p-1 ring-1 ring-slate-200" data-testid="mode-toggle">
                {[["freeflow", "Free Flow"], ["traditional", "ADW"]].map(([m, label]) => (
                  <button key={m} onClick={() => setMode(m)} data-testid={`mode-${m}`}
                    className={`px-5 py-1.5 text-sm font-semibold rounded-full transition-colors ${mode === m ? "bg-emerald-500 text-white shadow-sm" : "text-slate-600 hover:text-slate-900"}`}>{label}</button>
                ))}
              </div>
              {loadingEntries && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
            </div>

            <div className="h-8 w-px bg-slate-200" aria-hidden="true" />

            {/* Group 2: Primary actions */}
            <div className="flex items-center gap-2">
              <Button onClick={handleGenerate} disabled={generating}
                className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold px-6" data-testid="generate-btn">
                {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Tickets"}
              </Button>
              <Button onClick={clearAll} className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 gap-1.5" data-testid="clear-all-btn">
                Clear All
                <TooltipProvider delayDuration={150}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span onClick={(e) => e.stopPropagation()} data-testid="clear-all-info" className="inline-flex">
                        <Info className="w-3.5 h-3.5 text-white/80 hover:text-white" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      Will not affect Dont Use and Keys.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </Button>
            </div>

            {/* Group 3: Fill helpers (Free Flow only) */}
            {mode === "freeflow" && (
              <>
                <div className="h-8 w-px bg-slate-200" aria-hidden="true" />
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Fill</span>
                  <TooltipProvider delayDuration={150}>
                    <Button onClick={() => fillBestUnconstrained(maxPositions - 1)} variant="outline" disabled={maxPositions < 3}
                      className="border-slate-300 text-slate-700 font-semibold px-4 gap-1.5" data-testid="fill-2nd-last-btn">
                      Fill 2nd Last
                      {maxPositions >= 3 && <span className="text-xs font-normal text-slate-400" data-testid="fill-2nd-last-pos">→ Best {ordinal(maxPositions - 1)}</span>}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span onClick={(e) => e.stopPropagation()} data-testid="fill-2nd-last-info" className="inline-flex">
                            <Info className="w-3.5 h-3.5 text-slate-400 hover:text-slate-600" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          Sets <b>Best Finish = {ordinal(maxPositions - 1)}</b> for every active runner that isn't already constrained or set as a Key. One-shot apply.
                        </TooltipContent>
                      </Tooltip>
                    </Button>
                    <Button onClick={() => fillBestUnconstrained(maxPositions)} variant="outline"
                      className="border-slate-300 text-slate-700 font-semibold px-4 gap-1.5" data-testid="fill-last-btn">
                      Fill Last
                      <span className="text-xs font-normal text-slate-400" data-testid="fill-last-pos">→ Best {ordinal(maxPositions)}</span>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span onClick={(e) => e.stopPropagation()} data-testid="fill-last-info" className="inline-flex">
                            <Info className="w-3.5 h-3.5 text-slate-400 hover:text-slate-600" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          Sets <b>Best Finish = {ordinal(maxPositions)}</b> (last position) for every active runner that isn't already constrained or set as a Key. One-shot apply.
                        </TooltipContent>
                      </Tooltip>
                    </Button>
                  </TooltipProvider>
                </div>
              </>
            )}
          </div>
        </CardHeader>
        {!hintDismissed && entries.length > 0 && (
          <div className="mx-4 mb-2 flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800" data-testid="grid-hint">
            <Info className="w-4 h-4 mt-0.5 shrink-0 text-emerald-600" />
            <span className="flex-1">
              {mode === "freeflow"
                ? "Free Flow: every runner starts eligible for every position (all green). Tighten Best/Worst Finish (or Dont Use) to shape your tickets — or click the position dots directly to add/remove eligibility. Must specify at least one Key."
                : "ADW: check each runner into the positions you want it used in. Use ALL to box a runner, the column tools to fill/copy, and SWAP to exchange positions."}
            </span>
            <button onClick={dismissHint} title="Dismiss" data-testid="grid-hint-dismiss" className="shrink-0 text-emerald-600 hover:text-emerald-900">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        <CardContent className="p-0 overflow-x-auto">
          <Table className="w-auto [&_th]:h-8 [&_th]:py-0 [&_td]:py-0.5">
            <TableHeader>
              <TableRow className="border-slate-300 bg-slate-200">
                <TableHead className="text-slate-500 border-l border-slate-300">PN</TableHead>
                <TableHead className="text-slate-500 min-w-[200px]">Name</TableHead>
                <TableHead className="text-slate-500 text-right w-12 px-1">ML</TableHead>
                <TableHead className="text-slate-500 text-right w-12 px-1">Live</TableHead>
                <TableHead className="text-slate-500 text-center w-20 px-1">MyOdds</TableHead>
                <TableHead className="text-slate-500 text-right w-14 pl-1 pr-4">Proj</TableHead>
                {mode === "traditional" ? (
                  <>
                    <TableHead className="text-slate-500 text-center w-12 px-0 border-l-2 border-slate-300">All</TableHead>
                    {POS_LABELS.map((l, i) => {
                      const dead = i + 1 > maxPositions;
                      return (
                        <TableHead key={l} className={`text-center w-12 px-0 ${dead ? "text-slate-300" : "text-slate-500"}`}>{l}</TableHead>
                      );
                    })}
                  </>
                ) : (
                  <>
                    <TableHead className="text-center w-12 px-0 border-l-2 border-slate-300 text-slate-500" data-testid="win-head">Win</TableHead>
                    <TableHead className="text-center w-12 px-0 text-slate-500" data-testid="wp-head">WP</TableHead>
                    <TableHead className="text-center w-12 px-0 border-l-2 border-slate-300 text-slate-500" data-testid="key-head">Key</TableHead>
                    <TableHead className="text-center w-14 px-0">
                      <button onClick={resetBestAll} title="Reset All" data-testid="bestfin-reset"
                        className="inline-flex flex-col items-center gap-0.5 px-1.5 py-1 rounded border border-slate-300 bg-white text-slate-600 hover:bg-slate-100 hover:text-emerald-700 hover:border-emerald-400 transition-colors text-[11px] font-semibold leading-tight">
                        <span>Best<br />Finish</span> <RotateCcw className="w-3 h-3" />
                      </button>
                    </TableHead>
                    <TableHead className="text-center w-14 px-0">
                      <button onClick={resetWorstAll} title="Reset All" data-testid="worstfin-reset"
                        className="inline-flex flex-col items-center gap-0.5 px-1.5 py-1 rounded border border-slate-300 bg-white text-slate-600 hover:bg-slate-100 hover:text-emerald-700 hover:border-emerald-400 transition-colors text-[11px] font-semibold leading-tight">
                        <span>Worst<br />Finish</span> <RotateCcw className="w-3 h-3" />
                      </button>
                    </TableHead>
                    {POS_LABELS.map((l, i) => {
                      const dead = i + 1 > maxPositions;
                      return (
                        <TableHead key={l} className={`text-center w-14 px-0 ${dead ? "text-slate-300" : "text-slate-500"}`}>{l}</TableHead>
                      );
                    })}
                  </>
                )}
                <TableHead className="text-center w-14 px-0 text-red-400 leading-tight border-r-2 border-slate-300" data-testid="dontuse-head">Dont<br />Use</TableHead>
                <TableHead className="text-emerald-700 leading-tight text-center w-16 px-2 border-r border-slate-300">1MTP<br />Odds<br />Signal</TableHead>
                {WIN_RANK_ENABLED ? (
                  <TableHead className="text-emerald-700 text-center border border-slate-300 leading-tight w-16" data-testid="winrank-head">Win<br />Rank</TableHead>
                ) : (
                  <>
                    <PoolHead label="DBL" dropped={poolDropped.dbl} />
                    <PoolHead label="P3" dropped={poolDropped.p3} />
                    <PoolHead label="P4" dropped={poolDropped.p4} />
                    <PoolHead label="P5" dropped={poolDropped.p5} />
                  </>
                )}
              </TableRow>
              {mode === "traditional" && entries.length > 0 && (
                <TableRow className="border-slate-300 bg-slate-100" data-testid="col-controls-row">
                  <TableHead className="border-l border-slate-300" colSpan={6}>
                    <span className="text-[10px] text-slate-400 pl-1">column tools →</span>
                  </TableHead>
                  <TableHead className="text-center align-top" />
                  {POS_LABELS.map((l, i) => {
                    const pos = i + 1;
                    const dead = pos > maxPositions;
                    return (
                      <TableHead key={l} className="text-center align-top">
                        {!dead && (
                          <div className="flex items-center justify-center gap-1">
                            <button onClick={() => toggleColumn(pos)} data-testid={`col-all-${pos}`}
                              className="text-[10px] font-bold leading-none px-1.5 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700">ALL</button>
                            {pos < maxPositions && (
                              <button onClick={() => copyToNext(pos)} data-testid={`col-copy-${pos}`} title={`Copy ${l} → ${POS_LABELS[pos]}`}
                                className="text-[11px] font-bold leading-none px-1.5 py-1 rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-100">{">"}</button>
                            )}
                          </div>
                        )}
                      </TableHead>
                    );
                  })}
                  <TableHead colSpan={2 + POOL_COLS} className="border-r border-slate-300" />
                </TableRow>
              )}
            </TableHeader>
            <TableBody>
              {entries.map((e) => {
                const pn = String(e.programNumber);
                const s = grid[pn] || new Set();
                const scr = e.scratched;
                const du = !!dontUse[pn];
                const isAll = !du && [1, 2, 3, 4].every((p) => s.has(p));
                const dim = scr || du;
                const ffAllowed = allowedFor(pn);
                return (
                  <TableRow key={pn} className={`border-slate-300 hover:bg-slate-50 ${scr ? "opacity-40" : ""}`} data-testid={`runner-row-${pn}`}>
                    <TableCell className="border-l border-slate-300"><span className={pnClass(pn)}>{pn}</span></TableCell>
                    <TableCell className={`font-medium text-slate-900 min-w-[200px] ${scr ? "line-through" : ""} ${du ? "opacity-40" : ""}`}
                      title={[e.jockey, e.trainer].some(Boolean) ? `${e.jockey || "—"} | ${e.trainer || "—"}` : undefined}
                      data-testid={`bm-horse-name-${pn}`}>{e.horseName}</TableCell>
                    <TableCell className="text-slate-500 text-sm text-right w-12 px-1">{formatML(e.mlOdds)}</TableCell>
                    <TableCell className="text-slate-700 text-sm text-right w-12 px-1">{e.xmlOdds ? e.xmlOdds.toFixed(1) : "-"}</TableCell>
                    <TableCell className="text-center w-20 px-1">
                      <input type="text" inputMode="decimal" disabled={scr} value={e._myOdds ?? ""} placeholder="—"
                        onFocus={(ev) => ev.target.select()}
                        onChange={(ev) => {
                          let v = ev.target.value.replace(/[^0-9.]/g, "");
                          const dot = v.indexOf(".");
                          if (dot !== -1) v = v.slice(0, dot + 1) + v.slice(dot + 1).replace(/\./g, "").slice(0, 1);
                          if (v && v !== "." && parseFloat(v) > 99.9) v = "99.9";
                          setMyOdds(pn, v);
                        }}
                        className="w-14 h-7 rounded border border-slate-300 bg-white text-slate-900 text-sm text-center px-1 disabled:opacity-40" data-testid={`myodds-${pn}`} />
                    </TableCell>
                    <TableCell className="text-slate-700 text-sm text-right w-14 pl-1 pr-4">{e.projOdds ? e.projOdds.toFixed(1) : "-"}</TableCell>
                    {mode === "traditional" ? (
                      <>
                        <TableCell className="text-center w-12 px-0 border-l-2 border-slate-300">
                          <Checkbox checked={isAll} disabled={dim} onCheckedChange={() => toggleAll(pn)} data-testid={`all-${pn}`}
                            className="border-emerald-600 data-[state=checked]:bg-emerald-600 data-[state=checked]:text-white data-[state=checked]:border-emerald-600" />
                        </TableCell>
                        {POS_LABELS.map((l, i) => {
                          const dead = i + 1 > maxPositions;
                          return (
                            <TableCell key={l} className={`text-center w-12 px-0 ${du ? "opacity-30" : dead ? "opacity-40" : ""}`}>
                              <Checkbox checked={!du && s.has(i + 1)} disabled={dim} onCheckedChange={() => togglePos(pn, i + 1)} data-testid={`pos-${pn}-${i + 1}`} />
                            </TableCell>
                          );
                        })}
                      </>
                    ) : (
                      <>
                        <TableCell className="text-center w-12 px-0 border-l-2 border-slate-300">
                          <Checkbox checked={straightBet[pn] === "WIN"} disabled={dim} onCheckedChange={() => toggleRunnerStraight(pn, "WIN")} data-testid={`win-${pn}`}
                            className="border-slate-900 data-[state=checked]:bg-slate-900 data-[state=checked]:text-white data-[state=checked]:border-slate-900 disabled:opacity-40" />
                        </TableCell>
                        <TableCell className="text-center w-12 px-0">
                          <Checkbox checked={straightBet[pn] === "WP"} disabled={dim} onCheckedChange={() => toggleRunnerStraight(pn, "WP")} data-testid={`wp-${pn}`}
                            className="border-slate-900 data-[state=checked]:bg-slate-900 data-[state=checked]:text-white data-[state=checked]:border-slate-900 disabled:opacity-40" />
                        </TableCell>
                        <TableCell className="text-center w-12 px-0 border-l-2 border-slate-300">
                          <Checkbox checked={!du && !!keySel[pn]} disabled={dim} onCheckedChange={() => toggleKey(pn)} data-testid={`key-${pn}`}
                            className="border-orange-500 data-[state=checked]:bg-orange-500 data-[state=checked]:text-white data-[state=checked]:border-orange-500" />
                        </TableCell>
                        <TableCell className={`text-center w-14 px-0 ${du ? "opacity-30" : ""}`}>
                          <select value={bestFin[pn] ?? 0} disabled={dim} data-testid={`bestfin-${pn}`}
                            onChange={(ev) => setBest(pn, parseInt(ev.target.value, 10))}
                            className="h-7 w-12 rounded border border-slate-300 bg-white text-slate-900 text-xs text-center px-0 disabled:opacity-40">
                            <option value={0}>Any</option>
                            {Array.from({ length: allPositions }, (_, i) => i + 1).filter((n) => n >= 2).map((n) => <option key={n} value={n}>{POS_LABELS[n - 1]}</option>)}
                          </select>
                        </TableCell>
                        <TableCell className={`text-center w-14 px-0 ${du ? "opacity-30" : ""}`}>
                          <select value={worstFin[pn] ?? 0} disabled={dim} data-testid={`worstfin-${pn}`}
                            onChange={(ev) => setWorst(pn, parseInt(ev.target.value, 10))}
                            className="h-7 w-12 rounded border border-slate-300 bg-white text-slate-900 text-xs text-center px-0 disabled:opacity-40">
                            <option value={0}>Any</option>
                            {Array.from({ length: allPositions }, (_, i) => i + 1).filter((n) => n <= allPositions - 1).map((n) => <option key={n} value={n}>{POS_LABELS[n - 1]}</option>)}
                          </select>
                        </TableCell>
                        {POS_LABELS.map((l, i) => {
                          const pos = i + 1;
                          const dead = pos > maxPositions;
                          const inRange = !dead && !du && ffAllowed.includes(pos);
                          const clickable = !dead && !dim;
                          return (
                            <TableCell key={l} className={`text-center w-14 px-0 ${dead ? "opacity-40" : ""}`}>
                              {!dead && (
                                clickable ? (
                                  <button
                                    type="button"
                                    onClick={() => toggleDot(pn, pos)}
                                    data-testid={`ff-elig-${pn}-${pos}`}
                                    aria-pressed={inRange}
                                    title={inRange ? `Eligible for ${l} — click to remove` : `Not eligible for ${l} — click to add`}
                                    className={`inline-flex items-center justify-center w-5 h-5 rounded-full transition-colors ${inRange ? "bg-emerald-500 hover:bg-emerald-600 ring-1 ring-emerald-600" : "bg-transparent border border-dashed border-slate-300 hover:border-emerald-400 hover:bg-emerald-50"}`}>
                                    <span className={`w-2 h-2 rounded-full ${inRange ? "bg-white/90" : "bg-transparent"}`} />
                                  </button>
                                ) : (
                                  inRange && <span className="inline-block w-3.5 h-3.5 rounded-full bg-emerald-500" data-testid={`ff-elig-${pn}-${pos}`} />
                                )
                              )}
                            </TableCell>
                          );
                        })}
                      </>
                    )}
                    <TableCell className="text-center w-14 px-0 border-r-2 border-slate-300">
                      <Checkbox checked={du} disabled={scr} onCheckedChange={() => toggleDontUse(pn)} data-testid={`dontuse-${pn}`} />
                    </TableCell>
                    <TableCell className="w-16 px-2 border-r border-slate-300">
                      <div className="flex justify-center">
                        {!scr && e.mustUse && !suppressStrong && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700" data-testid={`signal-strong-${pn}`}>STRONG</span>}
                        {SHOW_OVERBET_FAV && !scr && e.overbetFav && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-violet-900 text-violet-200 border border-violet-700 inline-block text-center cursor-help" title={overbetTip(fieldSize)} data-testid={`signal-overbet-${pn}`}>OVERBET</span>}
                        {!scr && e.dontUse && !suppressFade && (() => {
                          const canFade = !dim && !(mode === "freeflow" && keySel[pn]) && (mode === "freeflow" || (mode === "traditional" && maxPositions >= 3));
                          const active = mode === "traditional"
                            ? ([3, 4].filter((p) => p <= maxPositions).every((p) => s.has(p)) && ![1, 2].some((p) => s.has(p)) && s.size > 0)
                            : (bestFin[pn] === 3);
                          if (!canFade) {
                            return <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-700 leading-tight text-center" data-testid={`signal-fade-${pn}`}>EXACTA FADE</span>;
                          }
                          return (
                            <button type="button"
                              onClick={() => mode === "traditional" ? setFadeUnderneathADW(pn) : setBest(pn, bestFin[pn] === 3 ? 0 : 3)}
                              title="Click to fade underneath (best finish 3rd). Click again to clear."
                              className={`text-[10px] font-bold px-1.5 py-0.5 rounded leading-tight text-center transition-colors cursor-pointer ${active ? "bg-red-600 text-white ring-2 ring-red-300" : "bg-red-100 text-red-700 hover:bg-red-200"}`}
                              data-testid={`signal-fade-${pn}`}>EXACTA FADE</button>
                          );
                        })()}
                      </div>
                    </TableCell>
                    {WIN_RANK_ENABLED ? (
                      <WinRankCell info={winRank[pn]} scratched={scr} pn={pn} />
                    ) : (
                      <>
                        <ScoreCell value={e.dblScore} uncovered={e.dblUncovered} poolDropped={poolDropped.dbl} colorMap={dblColors} pn={pn} scratched={scr} />
                        <ScoreCell value={e.p3Score} uncovered={e.p3Uncovered} poolDropped={poolDropped.p3} colorMap={p3Colors} pn={pn} scratched={scr} />
                        <ScoreCell value={e.p4Score} uncovered={e.p4Uncovered} poolDropped={poolDropped.p4} colorMap={p4Colors} pn={pn} scratched={scr} />
                        <ScoreCell value={e.p5Score} uncovered={e.p5Uncovered} poolDropped={poolDropped.p5} colorMap={p5Colors} pn={pn} scratched={scr} />
                      </>
                    )}
                  </TableRow>
                );
              })}
              {mode === "traditional" && entries.length > 0 && (
                <TableRow className="border-slate-300 bg-slate-50" data-testid="swap-row">
                  <TableCell colSpan={7} className="border-l border-slate-300" />
                  <TableCell colSpan={POS_LABELS.length} className="py-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-amber-600">SWAP</span>
                      {colPairs.map(([a, b]) => (
                        <Button key={`${a}-${b}`} size="sm" variant="outline"
                          className="h-9 px-4 text-sm font-semibold border-slate-300 text-slate-700"
                          onClick={() => swapCols(a, b)} data-testid={`swap-${a}-${b}`}>{a}-{b}</Button>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell colSpan={2 + POOL_COLS} className="border-r border-slate-300" />
                </TableRow>
              )}
              {entries.length === 0 && !loadingEntries && (
                <TableRow><TableCell colSpan={13 + POOL_COLS} className="text-center text-slate-400 py-8">Select a track and race to load runners.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
        {entries.length > 0 && (
          <div className="border-t border-slate-200 p-3" data-testid="selection-panel">
            {/* Live selection summary */}
            <div className="text-sm text-slate-700 font-mono" data-testid="selection-summary">
              {selectedPools.length === 0 ? (
                <span className="text-slate-400">Select a pool.</span>
              ) : (
                selectedPools.map((p) => {
                  const { count: elig, occ } = walkCombos(p.positions);
                  const pr = result?.pools?.find((x) => x.pool === p.code);
                  let played = null;
                  if (roundUp) played = elig;
                  else if (pr && pr.success) played = pr.ticketCount;
                  // Consolidated count — shown only when consolidation actually
                  // reduces the ticket count, using the same threshold gate as the
                  // AmWager/XpressBet download (wheel-collapse above admin threshold).
                  let consCount = null;
                  if (pr?.success && pr.tickets?.length) {
                    const wheel = shouldWheel(pr.tickets.length, consolThresholds[p.code], pr.poolMinimum, pr.ticketMin);
                    const c = consolidateTickets(pr.tickets, wheel).length;
                    if (played != null && c < played) consCount = c;
                  }
                  const isKey = (pn) => mode === "freeflow" && keyPns.includes(String(pn));
                  return (
                    <div key={p.code} data-testid={`summary-${p.code}`}>
                      <span className={`font-semibold text-slate-900 ${selectedPools.length > 1 ? "inline-block text-right w-[17rem]" : ""}`}>{p.name} Position Eligibility</span>{" · "}
                      {Array.from({ length: p.positions }, (_, i) => i + 1).map((pos) => {
                        const list = Array.from(occ[pos] || []).sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
                        return <span key={pos}>{POS_LABELS[pos - 1]}: <span className="text-emerald-700">{list.length ? list.map((pn, idx) => (
                          <span key={pn}>{isKey(pn) ? <span className="inline-flex items-center justify-center bg-orange-500 text-white font-bold rounded px-1.5 py-0.5 text-xs leading-none mx-0.5 align-middle" data-testid={`elig-key-${pos}-${pn}`}>{pn}</span> : pn}{idx < list.length - 1 ? "," : ""}</span>
                        )) : "—"}</span>{pos < p.positions ? "  ·  " : ""}</span>;
                      })}
                      {"  →  "}
                      <span className="text-amber-600 font-semibold" data-testid={`combos-${p.code}`}>Combinations: {elig} Eligible{played != null ? ` | ${played} Played` : ""}{consCount != null ? ` | ${consCount} Consolidated Tickets` : ""}</span>
                      {(() => {
                        if (!prefs.showTopUp || roundUp || !(p.code === "TRI" || p.code === "SPR") || !pr?.success) return null;
                        const min = pr.poolMinimum || 0;
                        if (!(min > 0)) return null;
                        if (toppedUp.includes(p.code)) {
                          const over = (pr.totalWager || 0) - resolvedAmt(p.code);
                          return over > 0 ? (
                            <span className="ml-2 text-xs font-semibold text-orange-600" data-testid={`complete-coverage-over-${p.code}`}>· over budget by ${over.toFixed(2)}</span>
                          ) : null;
                        }
                        if (played == null || played >= elig) return null;
                        const cost = (elig - played) * min;
                        return (
                          <TooltipProvider delayDuration={150}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button type="button" onClick={() => handleGenerate([...toppedUp, p.code])} disabled={generating}
                                  data-testid={`complete-coverage-${p.code}`}
                                  className="ml-2 inline-flex items-center gap-1 rounded-full bg-orange-500 hover:bg-orange-400 disabled:opacity-50 text-white text-xs font-bold px-2.5 py-0.5 transition-colors align-middle">
                                  Complete Coverage +${cost.toFixed(2)}
                                </button>
                              </TooltipTrigger>
                              <TooltipContent data-testid={`complete-coverage-tip-${p.code}`}>Add excluded combinations at pool minimum</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        );
                      })()}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </Card>

      {/* Race Payoffs shown by default once a race is official (before ticket generation). */}
      {racePayouts.length > 0 && !result?.pools && (
        <div className="mb-4 max-w-sm" data-testid="default-payoffs">
          <PayoffCard payouts={racePayouts} entries={entries} />
        </div>
      )}

      {/* Results */}
      {result?.pools && (
        <Card className="bg-white border-slate-200 shadow-sm" data-testid="betmaker-results">
          <CardHeader className="py-3 flex flex-row items-center justify-between">
            <div className="flex flex-col">
              <CardTitle className="text-base text-slate-900">Tickets — Total ${result.totalWager?.toFixed(2)}</CardTitle>
              {(() => {
                const play = result.pools.filter((p) => p.success).reduce((s, p) => s + actualPlayTotal(p.tickets, p.pool, p.poolMinimum, p.ticketMin, shouldWheel(p.tickets?.length, consolThresholds[p.pool], p.poolMinimum, p.ticketMin)).total, 0);
                const rounded = Math.round(play * 100) / 100;
                return rounded > (result.totalWager || 0) + 0.001 ? (
                  <span className="text-xs text-amber-600 font-medium mt-0.5" data-testid="actual-play-total">
                    Actual wager at the window: ${rounded.toFixed(2)} (tickets below the track's ticket minimum are raised to it)
                  </span>
                ) : null;
              })()}
            </div>
            <div className="flex gap-2">
              <AdwButton track={selectedTrack} race={selectedRace} date={selectedDate}
                getPools={() => result.pools.filter((p) => p.success && (p.tickets || []).length)
                  .reduce((a, p) => { a[p.pool] = p.tickets.map((t) => ({ combo: t.combo, amount: t.amount })); return a; }, {})}
                getKeys={() => Object.keys(keySel).filter((pn) => keySel[pn])}
                className="h-8 px-2 border-rose-400 text-rose-600 hover:bg-rose-50" />
              <Button size="sm" variant="outline" className="border-slate-300 text-slate-700" onClick={() => doExport("amwager")} data-testid="export-amwager"><Download className="w-4 h-4 mr-1" />AmWager</Button>
              <Button size="sm" variant="outline" className="border-slate-300 text-slate-700" onClick={() => doExport("xpressbet")} data-testid="export-xpressbet"><Download className="w-4 h-4 mr-1" />XpressBet</Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col lg:flex-row gap-4 items-start">
              <div className="flex-1 min-w-0 space-y-4">
                {result.pools.map((p) => {
              const payoutName = POOL_TO_PAYOUT[p.pool];
              const poolPayouts = (result.payouts || []).filter((pp) => pp.pool === payoutName);
              const graded = p.success && poolPayouts.length > 0;
              const matchFor = (combo) => poolPayouts.find((pp) => normalizeCombo(pp.winners) === normalizeCombo(combo)) || null;
              const wagered = p.totalWager || 0;
              const returned = graded ? p.tickets.reduce((s, t) => { const m = matchFor(t.combo); return s + (m ? t.amount * (m.payout || 0) : 0); }, 0) : 0;
              const net = returned - wagered;
              const roi = wagered > 0 ? (net / wagered) * 100 : 0;
              return (
                <div key={p.pool} className="rounded-lg border border-slate-200 p-3 bg-slate-50" data-testid={`pool-result-${p.pool}`}>
                  <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                    <div className="flex items-baseline gap-2">
                      <span className="font-semibold text-slate-900">{p.poolName || p.pool}</span>
                      <span className="text-sm text-slate-500">{p.success ? `${p.ticketCount} tix · $${p.totalWager?.toFixed(2)}` : "—"}</span>
                      {p.success && (() => {
                        const play = actualPlayTotal(p.tickets, p.pool, p.poolMinimum, p.ticketMin, shouldWheel(p.tickets?.length, consolThresholds[p.pool], p.poolMinimum, p.ticketMin));
                        return play.total > (p.totalWager || 0) + 0.001 ? (
                          <span className="text-xs text-amber-600 font-medium" data-testid={`play-total-${p.pool}`}>
                            → plays ${play.total.toFixed(2)} ({singleMinNote(p.pool, play.bumped, p.ticketMin)})
                          </span>
                        ) : null;
                      })()}
                    </div>
                    {graded && (
                      <div className="flex items-center gap-1.5 text-xs" data-testid={`roi-summary-${p.pool}`}>
                        <span className="rounded bg-white border border-slate-200 px-2 py-0.5"><span className="text-slate-400">Wagered </span><span className="font-bold text-slate-900">${wagered.toFixed(2)}</span></span>
                        <span className="rounded bg-white border border-slate-200 px-2 py-0.5"><span className="text-slate-400">Returned </span><span className={`font-bold ${returned > 0 ? "text-emerald-600" : "text-slate-500"}`}>${returned.toFixed(2)}</span></span>
                        <span className={`rounded px-2 py-0.5 border ${net >= 0 ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`}><span className="text-slate-400">ROI </span><span className={`font-bold ${net >= 0 ? "text-emerald-600" : "text-red-600"}`} data-testid={`roi-pct-${p.pool}`}>{net >= 0 ? "+" : ""}{roi.toFixed(0)}%</span></span>
                      </div>
                    )}
                  </div>
                  {p.success ? (
                    <div className="flex flex-wrap gap-x-1 gap-y-1 font-mono text-sm leading-relaxed" data-testid={`tickets-${p.pool}`}>
                      {p.tickets.map((t, i) => {
                        const m = graded ? matchFor(t.combo) : null;
                        const ret = m ? t.amount * (m.payout || 0) : 0;
                        return (
                          <span key={i} className="whitespace-nowrap" data-testid={`ticket-${p.pool}-${i}`}>
                            <span className={m ? "text-emerald-700 font-bold" : "text-slate-700"}>{t.combo}</span>
                            <span className="text-slate-400">,&nbsp;</span>
                            <span className="text-amber-600 font-semibold">{t.amount.toFixed(2)}</span>
                            {m && <span className="text-emerald-600 font-semibold" data-testid={`ticket-hit-${p.pool}-${i}`}>&nbsp;✓${ret.toFixed(2)}</span>}
                            {i < p.tickets.length - 1 && <span className="text-slate-300 px-1">|</span>}
                          </span>
                        );
                      })}
                    </div>
                  ) : <p className="text-xs text-red-600">{p.message}</p>}
                </div>
              );
            })}
            {result.straight && (result.straight.win.length > 0 || result.straight.wp.length > 0) && (() => {
              const grade = (result.payouts || []);
              const payFor = (name, pn) => {
                const m = grade.find((pp) => pp.pool === name && normalizeCombo(pp.winners) === normalizeCombo(pn));
                return m ? (m.payout || 0) : null;
              };
              return (
                <>
                  {result.straight.win.length > 0 && (() => {
                    const total = result.straight.win.reduce((s, t) => s + t.amount, 0);
                    const winGraded = grade.some((pp) => pp.pool === "win");
                    const returned = winGraded ? result.straight.win.reduce((s, t) => { const p = payFor("win", t.pn); return s + (p != null ? t.amount * p : 0); }, 0) : 0;
                    const net = returned - total, roi = total > 0 ? (net / total) * 100 : 0;
                    return (
                      <div className="rounded-lg border border-slate-200 p-3 bg-slate-50" data-testid="pool-result-WIN">
                        <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                          <div className="flex items-baseline gap-2">
                            <span className="font-semibold text-slate-900">Win</span>
                            <span className="text-sm text-slate-500">{result.straight.win.length} tix · ${total.toFixed(2)}</span>
                          </div>
                          {winGraded && (
                            <div className="flex items-center gap-1.5 text-xs" data-testid="roi-summary-WIN">
                              <span className="rounded bg-white border border-slate-200 px-2 py-0.5"><span className="text-slate-400">Wagered </span><span className="font-bold text-slate-900">${total.toFixed(2)}</span></span>
                              <span className="rounded bg-white border border-slate-200 px-2 py-0.5"><span className="text-slate-400">Returned </span><span className={`font-bold ${returned > 0 ? "text-emerald-600" : "text-slate-500"}`}>${returned.toFixed(2)}</span></span>
                              <span className={`rounded px-2 py-0.5 border ${net >= 0 ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`}><span className="text-slate-400">ROI </span><span className={`font-bold ${net >= 0 ? "text-emerald-600" : "text-red-600"}`} data-testid="roi-pct-WIN">{net >= 0 ? "+" : ""}{roi.toFixed(0)}%</span></span>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-x-1 gap-y-1 font-mono text-sm leading-relaxed" data-testid="tickets-WIN">
                          {result.straight.win.map((t, i) => {
                            const pay = payFor("win", t.pn);
                            const ret = pay != null ? t.amount * pay : 0;
                            return (
                              <span key={i} className="whitespace-nowrap" data-testid={`ticket-WIN-${i}`}>
                                <span className={pay != null ? "text-emerald-700 font-bold" : "text-slate-700"}>#{t.pn}</span>
                                <span className="text-slate-400">,&nbsp;</span>
                                <span className="text-amber-600 font-semibold">{t.amount.toFixed(2)}</span>
                                {pay != null && <span className="text-emerald-600 font-semibold">&nbsp;✓${ret.toFixed(2)}</span>}
                                {i < result.straight.win.length - 1 && <span className="text-slate-300 px-1">|</span>}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                  {result.straight.wp.length > 0 && (() => {
                    const legs = result.straight.wp.flatMap((t) => [
                      { pn: t.pn, gradePool: "win", label: "Win", amount: t.amount },
                      { pn: t.pn, gradePool: "place", label: "Place", amount: t.amount },
                    ]);
                    const total = legs.reduce((s, l) => s + l.amount, 0);
                    const wpGraded = grade.some((pp) => pp.pool === "win" || pp.pool === "place");
                    const returned = wpGraded ? legs.reduce((s, l) => { const p = payFor(l.gradePool, l.pn); return s + (p != null ? l.amount * p : 0); }, 0) : 0;
                    const net = returned - total, roi = total > 0 ? (net / total) * 100 : 0;
                    return (
                      <div className="rounded-lg border border-slate-200 p-3 bg-slate-50" data-testid="pool-result-WP">
                        <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                          <div className="flex items-baseline gap-2">
                            <span className="font-semibold text-slate-900">WP (Win + Place)</span>
                            <span className="text-sm text-slate-500">{legs.length} tix · ${total.toFixed(2)}</span>
                          </div>
                          {wpGraded && (
                            <div className="flex items-center gap-1.5 text-xs" data-testid="roi-summary-WP">
                              <span className="rounded bg-white border border-slate-200 px-2 py-0.5"><span className="text-slate-400">Wagered </span><span className="font-bold text-slate-900">${total.toFixed(2)}</span></span>
                              <span className="rounded bg-white border border-slate-200 px-2 py-0.5"><span className="text-slate-400">Returned </span><span className={`font-bold ${returned > 0 ? "text-emerald-600" : "text-slate-500"}`}>${returned.toFixed(2)}</span></span>
                              <span className={`rounded px-2 py-0.5 border ${net >= 0 ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`}><span className="text-slate-400">ROI </span><span className={`font-bold ${net >= 0 ? "text-emerald-600" : "text-red-600"}`} data-testid="roi-pct-WP">{net >= 0 ? "+" : ""}{roi.toFixed(0)}%</span></span>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-x-1 gap-y-1 font-mono text-sm leading-relaxed" data-testid="tickets-WP">
                          {legs.map((l, i) => {
                            const pay = payFor(l.gradePool, l.pn);
                            const ret = pay != null ? l.amount * pay : 0;
                            return (
                              <span key={i} className="whitespace-nowrap" data-testid={`ticket-WP-${i}`}>
                                <span className={pay != null ? "text-emerald-700 font-bold" : "text-slate-700"}>#{l.pn} {l.label}</span>
                                <span className="text-slate-400">,&nbsp;</span>
                                <span className="text-amber-600 font-semibold">{l.amount.toFixed(2)}</span>
                                {pay != null && <span className="text-emerald-600 font-semibold">&nbsp;✓${ret.toFixed(2)}</span>}
                                {i < legs.length - 1 && <span className="text-slate-300 px-1">|</span>}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                </>
              );
            })()}
              </div>
              {result.payouts && result.payouts.length > 0 && (
                <div className="w-full lg:w-72 shrink-0" data-testid="results-panel">
                  <PayoffCard payouts={result.payouts} entries={entries} />
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
